import { createSelector } from 'reselect';
// Import state types.
import {
	AppState,
	EndorsementState,
} from '@app/redux/states';
// Import interfaces.
import {
	IAddress,
} from '@app/interfaces';
// Import props.
import {
	BuildingProps,
	EndorsementProps,
	FormInputSettingsBuildingFullProps,
	FormInputSettingsBuildingInfoProps,
	FormInputSettingsCoverageLimitsAndDeductiblesPersonalProps,
	FormInputSettingsCoverageLimitsAndDeductiblesProps,
	FormInputSettingsDwellingInfoProps,
	FormInputSettingsInsuredInfoProps,
	FormInputSettingsMortgageeProps,
	FormInputSettingsPropertyAddressProps,
	MortgageeProps,
	OptionalMortgageeProps,
	SubformInputSettingsBuildingInfoAddressProps,
	SubformInputSettingsBuildingInfoProps,
	SubformInputSettingsBuildingLimitsProps,
	SubformInputSettingsDwellingInfoProps,
	SubformInputSettingsPropertyAddressProps,
} from '@app/props';
// Import api models.
import {
	Address,
    AddressLocationWithAccuracy,
	AllowedEndorsementDateRange,
	BuildingLimitsPersonal,
	BuildingLimitsPersonalWithRcv,
	BuildingResponseCommercialExisting,
	BuildingResponseCommercialExistingWithStatus,
	BuildingRiskDataPersonal,
	BuildingStatusInfo,
	ComparisonSummaryBuildings,
	ComparisonSummaryDwellingAddress,
	ComparisonSummaryDwellingDetails,
	ComparisonSummaryDwellingLimits,
	ComparisonSummaryEntity,
	ComparisonSummaryInsuredInfo,
	ComparisonSummaryMortgagees,
	ComparisonSummaryPerilDeductible,
	ComparisonSummaryPerilsDeductibles,
	DisallowedRequestField,
	DisqualifiersTextWithAcknowledgement,
	EndorsementPreSubmissionAcknowledgements,
	EndorsementResponse,
	EndorsementResponsePolicyCommercial,
	EndorsementResponsePolicyPersonal,
	EndorsementSection,
	EndorsementSubmissionStatusError,
	InsuredInfo,
	Mortgagee,
	MortgageeRequest,
	MortgageeStatusInfo,
	MortgageeWithStatus,
	PerilDeductible,
	PolicyCost,
	PolicyCostLineItem,
	PossibleDisclosureText,
	PossiblePeril,
} from '@app/models/api';
// Import enumerations.
import {
    EndorsementEntityField,
    EndorsementStatus,
	EndorsementSubmissionStatusReason,
	EntityInstanceType,
	Peril,
	RecordStatus,
	SectionStatus,
} from '@app/enumerations';
// Import selectors.
import {
	makeGetEndorsementAddBuildingSectionSelector,
	makeGetEndorsementAddMortgageeSectionSelector,
	makeGetEndorsementBuildingSectionsSelector,
	makeGetEndorsementDwellingLimitsSectionSelector,
	makeGetEndorsementDwellingPropertyAddressSectionSelector,
	makeGetEndorsementDwellingPropertyDetailsSectionSelector,
	makeGetEndorsementInsuredSectionSelector,
	makeGetEndorsementMortgageeSectionsSelector,
	makeGetEndorsementPerilsAndDeductiblesSectionSelector,
} from '@app/redux/selectors';

// Fetches the endorsement state.
const getEndorsementState = (state: AppState, props: EndorsementProps): EndorsementState | undefined =>
	state.endorsements[props.endorsementId] ?? undefined;


// Is Endorsement Loaded Selector.
const makeIsEndorsementLoadedSelector = () => createSelector(
	[getEndorsementState],
	(endorsementState: EndorsementState | undefined): boolean => 
		endorsementState?.isEndorsementLoaded ?? false,
	
);

// Get Endorsement Selector.
const makeGetEndorsementSelector = () => createSelector(
	[getEndorsementState],
	(endorsementState: EndorsementState | undefined): EndorsementResponse | undefined =>
		endorsementState?.endorsement ?? undefined,
);

// Can Endorsement Add Buildings Selector.
const makeCanEndorsementAddBuildingsSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): boolean =>
		endorsement?.areNewBuildingsAllowed ?? false,
);

// Can Endorsement Remove Buildings Selector.
const makeCanEndorsementRemoveBuildingsSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): boolean =>
		endorsement?.canBuildingsBeRemoved ?? false,
);

// Is Endorsement Renewal Selector.
const makeIsEndorsementRenewalSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): boolean =>
		endorsement?.applyAtRenewal ?? false,
);

// #region Old and New Value Selectors.

// Get Endorsement Old Policy Information Selector.
const makeGetEndorsementOldPolicyInformationSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined =>
		endorsement?.policyInformationOld ?? undefined
);

// Get Endorsement New Policy Information Selector.
const makeGetEndorsementNewPolicyInformationSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined =>
		endorsement?.policyInformationNew ?? undefined
);

// Get Endorsement Old Selected Perils Deductibles Selector.
const makeGetEndorsementOldSelectedPerilsDeductiblesSelector = () => createSelector(
	[makeGetEndorsementOldPolicyInformationSelector()],
	(oldPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): PerilDeductible[] =>
		oldPolicyInformation?.deductibles ?? [],
);

// Get Endorsement New Selected Perils Deductibles Selector.
const makeGetEndorsementNewSelectedPerilsDeductiblesSelector = () => createSelector(
	[makeGetEndorsementNewPolicyInformationSelector()],
	(newPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): PerilDeductible[] =>
		newPolicyInformation?.deductibles ?? [],
);

// Get Endorsement Old Selected Peril Codes Selector.
const makeGetEndorsementOldSelectedPerilCodesSelector = () => createSelector(
	[makeGetEndorsementOldSelectedPerilsDeductiblesSelector()],
	(oldSelectedPerilsDeductibles: PerilDeductible[]): Peril[] => {
		return oldSelectedPerilsDeductibles.map((oldSelectedPerilDeductible: PerilDeductible) => {
			return oldSelectedPerilDeductible.perilCode !== null
				? oldSelectedPerilDeductible.perilCode
				: Peril.NONE;
		}).filter((perilCode: Peril) => {
			return !String.isNullOrWhiteSpace(perilCode);
		});
	}
);

// Get Endorsement New Selected Peril Codes Selector.
const makeGetEndorsementNewSelectedPerilCodesSelector = () => createSelector(
	[makeGetEndorsementNewSelectedPerilsDeductiblesSelector()],
	(newSelectedPerilsDeductibles: PerilDeductible[]): Peril[] => {
		return newSelectedPerilsDeductibles.map((newSelectedPerilDeductible: PerilDeductible) => {
			return newSelectedPerilDeductible.perilCode !== null
				? newSelectedPerilDeductible.perilCode
				: Peril.NONE;
		}).filter((perilCode: Peril) => {
			return !String.isNullOrWhiteSpace(perilCode);
		});
	}
);

// Get Endorsement New And Old Union Selected Peril Codes Selector.
const makeGetEndorsementNewAndOldUnionSelectedPerilCodesSelector = () => createSelector(
	[makeGetEndorsementOldSelectedPerilCodesSelector(), makeGetEndorsementNewSelectedPerilCodesSelector()],
	(oldSelectedPerilCodes: Peril[], newSelectedPerilCodes: Peril[]): Peril[] =>
		[...new Set([...oldSelectedPerilCodes, ...newSelectedPerilCodes])]

);

// Get Endorsement Old Property Address Location With Accuracy Personal Selector.
const makeGetEndorsementOldPropertyAddressLocationWithAccuracyPersonalSelector = () => createSelector(
	[makeGetEndorsementOldPolicyInformationSelector()],
	(oldPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): AddressLocationWithAccuracy | undefined => {
		let oldPropertyAddress: AddressLocationWithAccuracy | undefined = undefined;
		if (oldPolicyInformation !== undefined && EndorsementResponsePolicyPersonal.isEndorsementResponsePolicyPersonal(oldPolicyInformation)) {
			oldPropertyAddress = oldPolicyInformation.propertyAddress ?? undefined;
		}
		return oldPropertyAddress;
	}
);

// Get Endorsement New Property Address Location With Accuracy Personal Selector.
const makeGetEndorsementNewPropertyAddressLocationWithAccuracyPersonalSelector = () => createSelector(
	[makeGetEndorsementNewPolicyInformationSelector()],
	(newPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): AddressLocationWithAccuracy | undefined => {
		let newPropertyAddress: AddressLocationWithAccuracy | undefined = undefined;
		if (newPolicyInformation !== undefined && EndorsementResponsePolicyPersonal.isEndorsementResponsePolicyPersonal(newPolicyInformation)) {
			newPropertyAddress = newPolicyInformation.propertyAddress ?? undefined;
		}
		return newPropertyAddress;
	}
);

// Get Endorsement Old Property Address Personal Selector.
const makeGetEndorsementOldPropertyAddressPersonalSelector = () => createSelector(
	[makeGetEndorsementOldPropertyAddressLocationWithAccuracyPersonalSelector()],
	(addressLocationWithAccuracy: AddressLocationWithAccuracy | undefined): Address | undefined => {
		return addressLocationWithAccuracy !== undefined
			&& addressLocationWithAccuracy.address !== null
			? addressLocationWithAccuracy.address
			: undefined;
	}
);

// Get Endorsement New Property Address Personal Selector.
const makeGetEndorsementNewPropertyAddressPersonalSelector = () => createSelector(
	[makeGetEndorsementNewPropertyAddressLocationWithAccuracyPersonalSelector()],
	(addressLocationWithAccuracy: AddressLocationWithAccuracy | undefined): Address | undefined => {
		return addressLocationWithAccuracy !== undefined
			&& addressLocationWithAccuracy.address !== null
			? addressLocationWithAccuracy.address
			: undefined;
	}
);

// Get Endorsement Old Building Risk Data Personal Selector.
const makeGetEndorsementOldBuildingRiskDataPersonalSelector = () => createSelector(
	[makeGetEndorsementOldPolicyInformationSelector()],
	(oldPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): BuildingRiskDataPersonal | undefined => {
		let oldBuildingRiskDataPersonal: BuildingRiskDataPersonal | undefined = undefined;
		if (oldPolicyInformation !== undefined && EndorsementResponsePolicyPersonal.isEndorsementResponsePolicyPersonal(oldPolicyInformation)) {
			oldBuildingRiskDataPersonal = oldPolicyInformation.buildingRiskData ?? undefined;
		}
		return oldBuildingRiskDataPersonal;
	}
);

// Get Endorsement New Building Risk Data Personal Selector.
const makeGetEndorsementNewBuildingRiskDataPersonalSelector = () => createSelector(
	[makeGetEndorsementNewPolicyInformationSelector()],
	(newPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): BuildingRiskDataPersonal | undefined => {
		let newBuildingRiskDataPersonal: BuildingRiskDataPersonal | undefined = undefined;
		if (newPolicyInformation !== undefined && EndorsementResponsePolicyPersonal.isEndorsementResponsePolicyPersonal(newPolicyInformation)) {
			newBuildingRiskDataPersonal = newPolicyInformation.buildingRiskData ?? undefined;
		}
		return newBuildingRiskDataPersonal;
	}
);

// Get Endorsement Old Building Limits Personal Selector.
const makeGetEndorsementOldBuildingLimitsPersonalSelector = () => createSelector(
	[makeGetEndorsementOldPolicyInformationSelector()],
	(oldPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): BuildingLimitsPersonal | undefined => {
		let oldBuildingLimitsPersonal: BuildingLimitsPersonal | undefined = undefined;
		if (oldPolicyInformation !== undefined && EndorsementResponsePolicyPersonal.isEndorsementResponsePolicyPersonal(oldPolicyInformation)) {
			oldBuildingLimitsPersonal = oldPolicyInformation.buildingLimitsPersonal ?? undefined;
		}
		return oldBuildingLimitsPersonal;
	}
);

// Get Endorsement New Building Limits Personal Selector.
const makeGetEndorsementNewBuildingLimitsPersonalSelector = () => createSelector(
	[makeGetEndorsementNewPolicyInformationSelector()],
	(newPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): BuildingLimitsPersonal | undefined => {
		let newBuildingLimitsPersonal: BuildingLimitsPersonal | undefined = undefined;
		if (newPolicyInformation !== undefined && EndorsementResponsePolicyPersonal.isEndorsementResponsePolicyPersonal(newPolicyInformation)) {
			newBuildingLimitsPersonal = newPolicyInformation.buildingLimitsPersonal ?? undefined;
		}
		return newBuildingLimitsPersonal;
	}
);

// Get Endorsement Old Has Flood Peril Selected Selector.
const makeGetEndorsementOldHasFloodPerilSelectedSelector = () => createSelector(
	[makeGetEndorsementOldSelectedPerilsDeductiblesSelector()],
	(oldPerilsDeductibles: PerilDeductible[]): boolean =>
		oldPerilsDeductibles.some((oldPerilDedutible: PerilDeductible) => oldPerilDedutible.perilCode === Peril.FLOOD),
);

// Get Endorsement New Has Flood Peril Selected Selector.
const makeGetEndorsementNewHasFloodPerilSelectedSelector = () => createSelector(
	[makeGetEndorsementNewSelectedPerilsDeductiblesSelector()],
	(newPerilsDeductibles: PerilDeductible[]): boolean =>
		newPerilsDeductibles.some((newPerilDedutible: PerilDeductible) => newPerilDedutible.perilCode === Peril.FLOOD),
);

// Get Endorsement Old Has Earthquake Peril Selected Selector.
const makeGetEndorsementOldHasEarthquakePerilSelectedSelector = () => createSelector(
	[makeGetEndorsementOldSelectedPerilsDeductiblesSelector()],
	(oldPerilsDeductibles: PerilDeductible[]): boolean =>
		oldPerilsDeductibles.some((oldPerilDedutible: PerilDeductible) => oldPerilDedutible.perilCode === Peril.EARTHQUAKE),
);

// Get Endorsement New Has Earthquake Peril Selected Selector.
const makeGetEndorsementNewHasEarthquakePerilSelectedSelector = () => createSelector(
	[makeGetEndorsementNewSelectedPerilsDeductiblesSelector()],
	(newPerilsDeductibles: PerilDeductible[]): boolean =>
		newPerilsDeductibles.some((newPerilDedutible: PerilDeductible) => newPerilDedutible.perilCode === Peril.EARTHQUAKE),
);

// Get Endorsement Old Buildings Selector.
const makeGetEndorsementOldBuildingsSelector = () => createSelector(
	[makeGetEndorsementOldPolicyInformationSelector()],
	(oldPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): BuildingResponseCommercialExisting[] =>
		oldPolicyInformation !== undefined && EndorsementResponsePolicyCommercial.isEndorsementResponsePolicyCommercial(oldPolicyInformation)
			? oldPolicyInformation.buildings
			: [],
);

// Get Endorsement New Buildings Selector.
const makeGetEndorsementNewBuildingsSelector = () => createSelector(
	[makeGetEndorsementNewPolicyInformationSelector()],
	(newPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): BuildingResponseCommercialExisting[] =>
		newPolicyInformation !== undefined && EndorsementResponsePolicyCommercial.isEndorsementResponsePolicyCommercial(newPolicyInformation)
			? newPolicyInformation.buildings
			: [],
);

// Get Endorsement New Building Ids Selector.
const makeGetEndorsementNewBuildingGuidsSelector = () => createSelector(
	[makeGetEndorsementNewBuildingsSelector()],
	(newBuildings: BuildingResponseCommercialExisting[]): string[] =>
		newBuildings
			.map<string>((newBuilding: BuildingResponseCommercialExisting) => newBuilding.id ?? String.empty())
			.filter((buildingGuid: string) => !String.isNullOrWhiteSpace(buildingGuid)),
);

// Get Endorsement Old Building Selector.
const makeGetEndorsementOldBuildingSelector = () => createSelector(
	[
		(state: AppState, props: BuildingProps) => props.buildingGuid,
		makeGetEndorsementOldBuildingsSelector()
	],
	(
		buildingGuid: string,
		buildings: BuildingResponseCommercialExisting[],
	): BuildingResponseCommercialExisting | undefined =>
		buildings.find((building) => building.id === buildingGuid)
);

// Get Endorsement New Building Selector.
const makeGetEndorsementNewBuildingSelector = () => createSelector(
	[
		(state: AppState, props: BuildingProps) => props.buildingGuid,
		makeGetEndorsementNewBuildingsSelector()
	],
	(
		buildingGuid: string,
		buildings: BuildingResponseCommercialExisting[],
	): BuildingResponseCommercialExisting | undefined =>
		buildings.find((building) => building.id === buildingGuid)
);

// Get Endorsement Old Building Address Location With Accuracy Commercial Selector.
const makeGetEndorsementOldBuildingAddressLocationWithAccuracyCommercialSelector = () => createSelector(
	[makeGetEndorsementOldBuildingSelector()],
	(oldBuilding: BuildingResponseCommercialExisting | undefined): AddressLocationWithAccuracy | undefined => {
		return oldBuilding?.address ?? undefined;
	}
);

// Get Endorsement New Building Address Location With Accuracy Commercial Selector.
const makeGetEndorsementNewBuildingAddressLocationWithAccuracyCommercialSelector = () => createSelector(
	[makeGetEndorsementNewBuildingSelector()],
	(newBuilding: BuildingResponseCommercialExisting | undefined): AddressLocationWithAccuracy | undefined => {
		return newBuilding?.address ?? undefined;
	}
);

// Get Endorsement Old Building Address Commercial Selector.
const makeGetEndorsementOldBuildingAddressCommercialSelector = () => createSelector(
	[makeGetEndorsementOldBuildingAddressLocationWithAccuracyCommercialSelector()],
	(addressLocationWithAccuracy: AddressLocationWithAccuracy | undefined): Address | undefined => {
		return addressLocationWithAccuracy?.address ?? undefined;
	}
);

// Get Endorsement New Building Address Commercial Selector.
const makeGetEndorsementNewBuildingAddressCommercialSelector = () => createSelector(
	[makeGetEndorsementNewBuildingAddressLocationWithAccuracyCommercialSelector()],
	(addressLocationWithAccuracy: AddressLocationWithAccuracy | undefined): Address | undefined => {
		return addressLocationWithAccuracy?.address ?? undefined;
	}
);

// Get Endorsement Old Mortgagees Selector.
const makeGetEndorsementOldMortgageesSelector = () => createSelector(
	[makeGetEndorsementOldPolicyInformationSelector()],
	(oldPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): Mortgagee[] =>
		oldPolicyInformation?.mortgagees ?? []
);

// Get Endorsement New Mortgagees Selector.
const makeGetEndorsementNewMortgageesSelector = () => createSelector(
	[makeGetEndorsementNewPolicyInformationSelector()],
	(newPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): Mortgagee[] =>
		newPolicyInformation?.mortgagees ?? []
);

// Get Endorsement New Mortgagee Guids Selector.
const makeGetEndorsementNewMortgageeGuidsSelector = () => createSelector(
	[makeGetEndorsementNewMortgageesSelector()],
	(newMortgagees: Mortgagee[]): string[] =>
		newMortgagees
			.map<string>((newMortagee: Mortgagee) => newMortagee.id ?? String.empty())
			.filter((mortgageeGuid: string) => !String.isNullOrWhiteSpace(mortgageeGuid))
);

// Get Endorsement New Mortgagee Lienholder Positions In Use Selector.
const makeGetEndorsementNewMortgageeLienholderPositionsInUseSelector = () => createSelector(
	[
		(state: AppState, props: OptionalMortgageeProps) => props.mortgageeGuid,
		makeGetEndorsementNewMortgageesSelector()
	],
	(mortgageeGuidToExclude: string | undefined, newMortgagees: Mortgagee[]): number[] =>
		newMortgagees
			.filter((newMortgagee: Mortgagee) => mortgageeGuidToExclude === undefined || newMortgagee.id !== mortgageeGuidToExclude)
			.map<number>((newMortagee: Mortgagee) => newMortagee.lienPositionNumber ?? 0)
			.filter((lienPositionNumber: number) => lienPositionNumber !== 0)
);

// Get Endorsement New Mortgagee Emails In Use Selector.
const makeGetEndorsementNewMortgageeEmailsInUseSelector = () => createSelector(
	[
		(state: AppState, props: OptionalMortgageeProps) => props.mortgageeGuid,
		makeGetEndorsementNewMortgageesSelector()
	],
	(mortgageeGuidToExclude: string | undefined, newMortgagees: Mortgagee[]): string[] =>
		newMortgagees
			.filter((newMortgagee: Mortgagee) => mortgageeGuidToExclude === undefined || newMortgagee.id !== mortgageeGuidToExclude)
			.map<string>((newMortagee: Mortgagee) => newMortagee.email ?? String.empty())
			.filter((email: string) => !String.isNullOrWhiteSpace(email))
);

// Get Endorsement New Mortgagee Selector.
const makeGetEndorsementNewMortgageeSelector = () => createSelector(
	[
		(state: AppState, props: MortgageeProps) => props.mortgageeGuid,
		makeGetEndorsementNewMortgageesSelector()
	],
	(mortgageeGuid: string, newEndorsementMortgagees: Mortgagee[]): Mortgagee | undefined =>
		newEndorsementMortgagees.find((newEndorsementMortgagee: Mortgagee) => newEndorsementMortgagee.id === mortgageeGuid)
);


// Get Endorsement New Mortgagee As Request Selector.
const makeGetEndorsementNewMortgageeAsRequestSelector = () => createSelector(
	[makeGetEndorsementNewMortgageeSelector()],
	(newEndorsementMortgagee: Mortgagee | undefined): MortgageeRequest | undefined => {
		let newEndorsementMortgageeRequest: MortgageeRequest | undefined = undefined;
		if (newEndorsementMortgagee !== undefined) {
			newEndorsementMortgageeRequest = new MortgageeRequest();
			newEndorsementMortgageeRequest.name = newEndorsementMortgagee.name;
			newEndorsementMortgageeRequest.includeSuccessorOrAssigns = newEndorsementMortgagee.includeItsSuccessorsAndOrAssigns;
			newEndorsementMortgageeRequest.includeInterests = newEndorsementMortgagee.includeAsTheirInterestsMayAppear;
			newEndorsementMortgageeRequest.loanNumber = newEndorsementMortgagee.loanNumber;
			newEndorsementMortgageeRequest.positionNumber = newEndorsementMortgagee.lienPositionNumber;
			newEndorsementMortgageeRequest.isMortgageeBilled = newEndorsementMortgagee.billMortgagee;
			newEndorsementMortgageeRequest.isMortgageeRequired = newEndorsementMortgagee.coverageRequiredByMortgagee;
			newEndorsementMortgageeRequest.address = newEndorsementMortgagee.mailingAddress;
			newEndorsementMortgageeRequest.email = newEndorsementMortgagee.email;
			newEndorsementMortgageeRequest.sendPaymentReceiptToMortgagee = newEndorsementMortgagee.sendPaymentReceiptToEmail;
		}
		return newEndorsementMortgageeRequest;
	}
);

// Get Endorsement Old Property State Selector.
const makeGetEndorsementOldPropertyStateSelector = () => createSelector(
	[makeGetEndorsementOldPolicyInformationSelector()],
	(oldPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): string | undefined => {
		let propertyState: string | undefined = undefined;
		if (oldPolicyInformation !== undefined) {
			if (EndorsementResponsePolicyPersonal.isEndorsementResponsePolicyPersonal(oldPolicyInformation)) {
				propertyState = oldPolicyInformation.propertyAddress?.address?.state ?? undefined;
			}
			else if (EndorsementResponsePolicyCommercial.isEndorsementResponsePolicyCommercial(oldPolicyInformation) && oldPolicyInformation.buildings.length > 0) {
				let propertyStates: string[] = oldPolicyInformation.buildings.map((building: BuildingResponseCommercialExisting) => {
					return building.address?.address?.state ?? String.empty();
				}).filter((propertyState: string | undefined) => {
					return !String.isNullOrWhiteSpace(propertyState);
				});
				propertyState = propertyStates.length > 0 ? propertyStates[0] : undefined;
			}
		}
		return propertyState;
	}
);

// Get Endorsement New Property State Selector.
const makeGetEndorsementNewPropertyStateSelector = () => createSelector(
	[makeGetEndorsementNewPolicyInformationSelector()],
	(newPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): string | undefined => {
		let propertyState: string | undefined = undefined;
		if (newPolicyInformation !== undefined) {
			if (EndorsementResponsePolicyPersonal.isEndorsementResponsePolicyPersonal(newPolicyInformation)) {
				propertyState = newPolicyInformation.propertyAddress?.address?.state ?? undefined;
			}
			else if (EndorsementResponsePolicyCommercial.isEndorsementResponsePolicyCommercial(newPolicyInformation) && newPolicyInformation.buildings.length > 0) {
				let propertyStates: string[] = newPolicyInformation.buildings.map((building: BuildingResponseCommercialExisting) => {
					return building.address?.address?.state ?? String.empty();
				}).filter((propertyState: string | undefined) => {
					return !String.isNullOrWhiteSpace(propertyState);
				});
				propertyState = propertyStates.length > 0 ? propertyStates[0] : undefined;
			}
		}
		return propertyState;
	}
);

// Get Endorsement Old Insured Info Selector.
const makeGetEndorsementOldInsuredInfoSelector = () => createSelector(
	[makeGetEndorsementOldPolicyInformationSelector()],
	(oldPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): InsuredInfo | undefined =>
		oldPolicyInformation?.namedInsured ?? undefined,
);

// Get Endorsement New Insured Info Selector.
const makeGetEndorsementNewInsuredInfoSelector = () => createSelector(
	[makeGetEndorsementNewPolicyInformationSelector()],
	(newPolicyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): InsuredInfo | undefined =>
		newPolicyInformation?.namedInsured ?? undefined,
);

// #endregion Old and New Value Selectors.

// #region Comparison Selectors.

// Get Endorsement Current Comparison Peril Deductible Summaries Selector.
const makeGetEndorsementCurrentComparisonPerilDecuctibleSummariesSelector = () => createSelector(
	[
		makeGetEndorsementOldSelectedPerilsDeductiblesSelector(),
		makeGetEndorsementNewSelectedPerilsDeductiblesSelector(),
	],
	(
		oldSelectedPerilsDeductibles: PerilDeductible[],
		newSelectedPerilsDeductibles: PerilDeductible[],
	): ComparisonSummaryPerilDeductible[] => {
		return newSelectedPerilsDeductibles.filter((newSelectedPerilDeductible: PerilDeductible) => {
			return oldSelectedPerilsDeductibles.some((oldSelectedPerilDeductible: PerilDeductible) => {
				return oldSelectedPerilDeductible.perilCode !== null
					&& newSelectedPerilDeductible.perilCode !== null
					&& oldSelectedPerilDeductible.perilCode === newSelectedPerilDeductible.perilCode;
			});
		}).map((currentSelectedPerilDeductible: PerilDeductible) => {
			let oldSelectedPerilDeductible: PerilDeductible | undefined = oldSelectedPerilsDeductibles
				.find((oldSelectedPerilDeductible: PerilDeductible) => {
					return oldSelectedPerilDeductible.perilCode !== null
						&& currentSelectedPerilDeductible.perilCode !== null
						&& oldSelectedPerilDeductible.perilCode === currentSelectedPerilDeductible.perilCode;
				});

			return new ComparisonSummaryPerilDeductible({
				perilCode: oldSelectedPerilDeductible?.perilCode ?? currentSelectedPerilDeductible.perilCode ?? Peril.NONE,
				oldSelectedPerilDeductible: oldSelectedPerilDeductible,
				newSelectedPerilDeductible: currentSelectedPerilDeductible,
			});
		});
	},
);

// Get Endorsement Added Comparison Peril Deductible Summaries Selector.
const makeGetEndorsementAddedComparisonPerilDecuctibleSummariesSelector = () => createSelector(
	[
		makeGetEndorsementOldSelectedPerilsDeductiblesSelector(),
		makeGetEndorsementNewSelectedPerilsDeductiblesSelector(),
	],
	(
		oldSelectedPerilsDeductibles: PerilDeductible[],
		newSelectedPerilsDeductibles: PerilDeductible[],
	): ComparisonSummaryPerilDeductible[] => {
		return newSelectedPerilsDeductibles.filter((newSelectedPerilDeductible: PerilDeductible) => {
			return !oldSelectedPerilsDeductibles.some((oldSelectedPerilDeductible: PerilDeductible) => {
				return oldSelectedPerilDeductible.perilCode !== null
					&& newSelectedPerilDeductible.perilCode !== null
					&& oldSelectedPerilDeductible.perilCode === newSelectedPerilDeductible.perilCode;
			});
		}).map((addedSelectedPerilDeductible: PerilDeductible) => {
			return new ComparisonSummaryPerilDeductible({
				perilCode: addedSelectedPerilDeductible.perilCode ?? Peril.NONE,
				oldSelectedPerilDeductible: undefined,
				newSelectedPerilDeductible: addedSelectedPerilDeductible,
			});
		});
	},
);

// Get Endorsement Removed Comparison Peril Deductible Summaries Selector.
const makeGetEndorsementRemovedComparisonPerilDecuctibleSummariesSelector = () => createSelector(
	[
		makeGetEndorsementOldSelectedPerilsDeductiblesSelector(),
		makeGetEndorsementNewSelectedPerilsDeductiblesSelector(),
	],
	(
		oldSelectedPerilsDeductibles: PerilDeductible[],
		newSelectedPerilsDeductibles: PerilDeductible[],
	): ComparisonSummaryPerilDeductible[] => {
		return oldSelectedPerilsDeductibles.filter((oldSelectedPerilDeductible: PerilDeductible) => {
			return !newSelectedPerilsDeductibles.some((newSelectedPerilDeductible: PerilDeductible) => {
				return oldSelectedPerilDeductible.perilCode !== null
					&& newSelectedPerilDeductible.perilCode !== null
					&& oldSelectedPerilDeductible.perilCode === newSelectedPerilDeductible.perilCode;
			});
		}).map((removedSelectedPerilDeductible: PerilDeductible) => {
			return new ComparisonSummaryPerilDeductible({
				perilCode: removedSelectedPerilDeductible.perilCode ?? Peril.NONE,
				oldSelectedPerilDeductible: removedSelectedPerilDeductible,
				newSelectedPerilDeductible: undefined,
			});
		});
	},
);

// Get Endorsement Comparison Peril Deductible Summaries Ordered Selector.
const makeGetEndorsementComparisonPerilDeductibleSummariesOrderedSelector = () => createSelector(
	[
		makeGetEndorsementCurrentComparisonPerilDecuctibleSummariesSelector(),
		makeGetEndorsementAddedComparisonPerilDecuctibleSummariesSelector(),
		makeGetEndorsementRemovedComparisonPerilDecuctibleSummariesSelector(),
	],
	(
		currentComparisonPerilDeductibleSummaries: ComparisonSummaryPerilDeductible[],
		addedComparisonPerilDeductibleSummaries: ComparisonSummaryPerilDeductible[],
		removedComparisonPerilDeductibleSummaries: ComparisonSummaryPerilDeductible[],
	): ComparisonSummaryPerilDeductible[] => {
		return [
			...currentComparisonPerilDeductibleSummaries,
			...addedComparisonPerilDeductibleSummaries,
			...removedComparisonPerilDeductibleSummaries,
		].sort((comparisonPerilDeductibleSummaryA: ComparisonSummaryPerilDeductible, comparisonPerilDeductibleSummaryB: ComparisonSummaryPerilDeductible) => {
			if (comparisonPerilDeductibleSummaryA.perilCode > comparisonPerilDeductibleSummaryB.perilCode) {
				return 1;
			}
			else if (comparisonPerilDeductibleSummaryA.perilCode < comparisonPerilDeductibleSummaryB.perilCode) {
				return -1;
			}
			return 0;
		});
	},
);

// Get Endorsement Comparison Perils Deductibles Summary Selector.
const makeGetEndorsementComparisonPerilsDeductiblesSummarySelector = () => createSelector(
	[
		makeGetEndorsementComparisonPerilDeductibleSummariesOrderedSelector(),
		makeGetEndorsementPerilsAndDeductiblesSectionSelector(),
	],
	(
		comparisonPerilDeductbileSummaries: ComparisonSummaryPerilDeductible[],
		endorsementPerilsAndDeductiblesSection: EndorsementSection,
	): ComparisonSummaryPerilsDeductibles => {
		return new ComparisonSummaryPerilsDeductibles({
			allowEdits: endorsementPerilsAndDeductiblesSection.isEditable ?? false,
			requiresReview: endorsementPerilsAndDeductiblesSection.status?.statusCode === SectionStatus.REQUIRED,
			comparisonPerilDeductibleSummaries: comparisonPerilDeductbileSummaries,
		});
	},
);

// Get Endorsement Comparison Dwelling Address Summary Selector.
const makeGetEndorsementComparisonDwellingAddressSummarySelector = () => createSelector(
	[
		makeGetEndorsementOldPropertyAddressLocationWithAccuracyPersonalSelector(),
		makeGetEndorsementNewPropertyAddressLocationWithAccuracyPersonalSelector(),
		makeGetEndorsementDwellingPropertyAddressSectionSelector(),
	],
	(
		oldPropertyAddress: AddressLocationWithAccuracy | undefined,
		newPropertyAddress: AddressLocationWithAccuracy | undefined,
		endorsementDwellingPropertyAddressSection: EndorsementSection,
	): ComparisonSummaryDwellingAddress => {
		return new ComparisonSummaryDwellingAddress({
			allowEdits: endorsementDwellingPropertyAddressSection.isEditable ?? false,
			requiresReview: endorsementDwellingPropertyAddressSection.status?.statusCode === SectionStatus.REQUIRED,
			oldData: oldPropertyAddress,
			newData: newPropertyAddress,
		});
	},
);

// Get Endorsement Comparison Dwelling Details Summary Selector.
const makeGetEndorsementComparisonDwellingDetailsSummarySelector = () => createSelector(
	[
		makeGetEndorsementOldBuildingRiskDataPersonalSelector(),
		makeGetEndorsementNewBuildingRiskDataPersonalSelector(),
		makeGetEndorsementOldHasFloodPerilSelectedSelector(),
		makeGetEndorsementNewHasFloodPerilSelectedSelector(),
		makeGetEndorsementOldHasEarthquakePerilSelectedSelector(),
		makeGetEndorsementNewHasEarthquakePerilSelectedSelector(),
		makeGetEndorsementDwellingPropertyDetailsSectionSelector(),
	],
	(
		oldBuildingRiskData: BuildingRiskDataPersonal | undefined,
		newBuildingRiskData: BuildingRiskDataPersonal | undefined,
		oldHasFloodPerilSelected: boolean,
		newHasFloodPerilSelected: boolean,
		oldHasEarthquakePerilSelected: boolean,
		newHasEarthquakePerilSelected: boolean,
		endorsementDwellingPropertyDetailSection: EndorsementSection,
	): ComparisonSummaryDwellingDetails => {
		return new ComparisonSummaryDwellingDetails({
			allowEdits: endorsementDwellingPropertyDetailSection.isEditable ?? false,
			requiresReview: endorsementDwellingPropertyDetailSection.status?.statusCode === SectionStatus.REQUIRED,
			oldHasFloodPerilSelected: oldHasFloodPerilSelected,
			newHasFloodPerilSelected: newHasFloodPerilSelected,
			oldHasEarthquakePerilSelected: oldHasEarthquakePerilSelected,
			newHasEarthquakePerilSelected: newHasEarthquakePerilSelected,
			oldData: oldBuildingRiskData,
			newData: newBuildingRiskData,
		});
	},
);

// Get Endorsement Comparison Dwelling Limits Summary Selector.
const makeGetEndorsementComparisonDwellingLimitsSummarySelector = () => createSelector(
	[
		makeGetEndorsementOldBuildingLimitsPersonalSelector(),
		makeGetEndorsementNewBuildingLimitsPersonalSelector(),
		makeGetEndorsementOldSelectedPerilsDeductiblesSelector(),
		makeGetEndorsementNewSelectedPerilsDeductiblesSelector(),
		makeGetEndorsementDwellingLimitsSectionSelector(),
	],
	(
		oldBuildingLimits: BuildingLimitsPersonal | undefined,
		newBuildingLimits: BuildingLimitsPersonal | undefined,
		oldSelectedPerilsDeductibles: PerilDeductible[],
		newSelectedPerilsDeductibles: PerilDeductible[],
		endorsementDwellingLimitsSection: EndorsementSection,
	): ComparisonSummaryDwellingLimits => {
		return new ComparisonSummaryDwellingLimits({
			allowEdits: endorsementDwellingLimitsSection.isEditable ?? false,
			requiresReview: endorsementDwellingLimitsSection.status?.statusCode === SectionStatus.REQUIRED,
			oldData: oldBuildingLimits,
			newData: newBuildingLimits,
			oldSelectedPerilsDeductibles,
			newSelectedPerilsDeductibles,
		});
	},
);

// Get Endorsement Current Comparison Building Summaries Selector.
const makeGetEndorsementCurrentComparisonBuildingSummariesSelector = () => createSelector(
	[
		makeGetEndorsementOldBuildingsSelector(),
		makeGetEndorsementNewBuildingsSelector(),
		makeGetEndorsementBuildingSectionsSelector(),
		makeCanEndorsementRemoveBuildingsSelector(),
	],
	(
		oldBuildings: BuildingResponseCommercialExisting[],
		newBuildings: BuildingResponseCommercialExisting[],
		endorsementBuildingSections: EndorsementSection[],
		canEndorsementRemoveBuildings: boolean,
	): ComparisonSummaryEntity<BuildingResponseCommercialExisting>[] => {
		return newBuildings.filter((newBuilding: BuildingResponseCommercialExisting) => {
			return oldBuildings.some((oldBuilding: BuildingResponseCommercialExisting) => {
				return oldBuilding.id !== null
					&& newBuilding.id !== null
					&& oldBuilding.id === newBuilding.id;
			});
		}).map((currentBuilding: BuildingResponseCommercialExisting) => {
			let oldBuilding: BuildingResponseCommercialExisting | undefined = oldBuildings
				.find((oldBuilding: BuildingResponseCommercialExisting) => {
					return oldBuilding.id !== null
						&& currentBuilding.id !== null
						&& oldBuilding.id === currentBuilding.id;
				});

			const matchingBuildingSection: EndorsementSection | undefined = endorsementBuildingSections.find((endorsementBuildingSection: EndorsementSection) => {
				return currentBuilding.id !== null
					&& endorsementBuildingSection.key?.toLowerCase() === currentBuilding.id.toLowerCase();
			});
			const isSectionEditable: boolean = matchingBuildingSection?.isEditable ?? false;
			const requiresReview: boolean = matchingBuildingSection?.status?.statusCode === SectionStatus.REQUIRED;

			return new ComparisonSummaryEntity<BuildingResponseCommercialExisting>({
				id: currentBuilding.id ?? oldBuilding?.id ?? String.empty(),
				descriptor: `${currentBuilding.buildingData?.buildingDescription}`,
				requiresReview: requiresReview,
				allowEdits: isSectionEditable,
				allowRemoval: canEndorsementRemoveBuildings,
				oldData: oldBuilding,
				newData: currentBuilding,
			});
		});
	},
);

// Get Endorsement Added Comparison Building Summaries Selector.
const makeGetEndorsementAddedComparisonBuildingSummariesSelector = () => createSelector(
	[
		makeGetEndorsementOldBuildingsSelector(),
		makeGetEndorsementNewBuildingsSelector(),
		makeGetEndorsementBuildingSectionsSelector(),
		makeCanEndorsementRemoveBuildingsSelector(),
	],
	(
		oldBuildings: BuildingResponseCommercialExisting[],
		newBuildings: BuildingResponseCommercialExisting[],
		endorsementBuildingSections: EndorsementSection[],
		canEndorsementRemoveBuildings: boolean,
	): ComparisonSummaryEntity<BuildingResponseCommercialExisting>[] => {
		return newBuildings.filter((newBuilding: BuildingResponseCommercialExisting) => {
			return !oldBuildings.some((oldBuilding: BuildingResponseCommercialExisting) => {
				return oldBuilding.id !== null
					&& newBuilding.id !== null
					&& oldBuilding.id === newBuilding.id;
			});
		}).map((addedBuilding: BuildingResponseCommercialExisting) => {
			const matchingBuildingSection: EndorsementSection | undefined = endorsementBuildingSections.find((endorsementBuildingSection: EndorsementSection) => {
				return addedBuilding.id !== null
					&& endorsementBuildingSection.key?.toLowerCase() === addedBuilding.id.toLowerCase();
			});
			const isSectionEditable: boolean = matchingBuildingSection?.isEditable ?? false;
			const requiresReview: boolean = matchingBuildingSection?.status?.statusCode === SectionStatus.REQUIRED;

			return new ComparisonSummaryEntity<BuildingResponseCommercialExisting>({
				id: addedBuilding.id ?? String.empty(),
				descriptor: `${addedBuilding.buildingData?.buildingDescription}`,
				requiresReview: requiresReview,
				allowEdits: isSectionEditable,
				allowRemoval: canEndorsementRemoveBuildings,
				oldData: undefined,
				newData: addedBuilding,
			});
		});
	},
);

// Get Endorsement Removed Comparison Building Summaries Selector.
const makeGetEndorsementRemovedComparisonBuildingSummariesSelector = () => createSelector(
	[
		makeGetEndorsementOldBuildingsSelector(),
		makeGetEndorsementNewBuildingsSelector(),
	],
	(
		oldBuildings: BuildingResponseCommercialExisting[],
		newBuildings: BuildingResponseCommercialExisting[],
	): ComparisonSummaryEntity<BuildingResponseCommercialExisting>[] => {
		return oldBuildings.filter((oldBuilding: BuildingResponseCommercialExisting) => {
			return !newBuildings.some((newBuilding: BuildingResponseCommercialExisting) => {
				return oldBuilding.id !== null
					&& newBuilding.id !== null
					&& oldBuilding.id.toLowerCase() === newBuilding.id.toLowerCase();
			});
		}).map((removedBuilding: BuildingResponseCommercialExisting) => {
			return new ComparisonSummaryEntity<BuildingResponseCommercialExisting>({
				id: removedBuilding.id ?? String.empty(),
				descriptor: `${removedBuilding.buildingData?.buildingDescription}`,
				requiresReview: false,
				allowEdits: false,
				allowRemoval: false,
				oldData: removedBuilding,
				newData: undefined,
			});
		});
	},
);

// Get Endorsement Comparison Building Summaries Selector.
const makeGetEndorsementComparisonBuildingSummariesSelector = () => createSelector(
	[
		makeGetEndorsementOldBuildingsSelector(),
		makeGetEndorsementCurrentComparisonBuildingSummariesSelector(),
		makeGetEndorsementAddedComparisonBuildingSummariesSelector(),
		makeGetEndorsementRemovedComparisonBuildingSummariesSelector(),
	],
	(
		oldBuildings: BuildingResponseCommercialExisting[],
		currentComparisonBuildingSummaries: ComparisonSummaryEntity<BuildingResponseCommercialExisting>[],
		addedComparisonBuildingSummaries: ComparisonSummaryEntity<BuildingResponseCommercialExisting>[],
		removedComparisonBuildingSummaries: ComparisonSummaryEntity<BuildingResponseCommercialExisting>[],
	): ComparisonSummaryEntity<BuildingResponseCommercialExisting>[] => {

		let buildingSummaries: ComparisonSummaryEntity<BuildingResponseCommercialExisting>[] = oldBuildings.map((oldBuilding) => {
			return currentComparisonBuildingSummaries.find((currentBuildingSummary) => currentBuildingSummary.id === oldBuilding.id)
				?? removedComparisonBuildingSummaries.find((removedBuildingSummary) => removedBuildingSummary.id === oldBuilding.id);
		}).filter<ComparisonSummaryEntity<BuildingResponseCommercialExisting>>((buildingSummary): buildingSummary is ComparisonSummaryEntity<BuildingResponseCommercialExisting> => {
			return buildingSummary !== undefined;
		});

		return [
			...buildingSummaries,
			...addedComparisonBuildingSummaries,
		].map((buildingSummary, index) => {
			buildingSummary.descriptor = `Building Number: ${index + 1} - ${buildingSummary.descriptor}`;
			return buildingSummary;
		});
	},
);

// Get Endorsement Comparison Buildings Summary Selector.
const makeGetEndorsementComparisonBuildingsSummarySelector = () => createSelector(
	[
		makeGetEndorsementComparisonBuildingSummariesSelector(),
		makeGetEndorsementOldHasFloodPerilSelectedSelector(),
		makeGetEndorsementNewHasFloodPerilSelectedSelector(),
		makeGetEndorsementAddBuildingSectionSelector(),
	],
	(
		comparisonBuildingSummaries: ComparisonSummaryEntity<BuildingResponseCommercialExisting>[],
		oldHasFloodPerilSelected: boolean,
		newHasFloodPerilSelected: boolean,
		endorsementAddBuildingSection: EndorsementSection,
	): ComparisonSummaryBuildings => {
		return new ComparisonSummaryBuildings({
			allowAdds: endorsementAddBuildingSection.isEditable ?? false,
			oldHasFloodPerilSelected: oldHasFloodPerilSelected,
			newHasFloodPerilSelected: newHasFloodPerilSelected,
			comparisonEntitySummaries: comparisonBuildingSummaries,
		});
	},
);

// Get Endorsement Current Comparison Mortgagee Summaries Selector.
const makeGetEndorsementCurrentComparisonMortgageeSummariesSelector = () => createSelector(
	[
		makeGetEndorsementOldMortgageesSelector(),
		makeGetEndorsementNewMortgageesSelector(),
		makeGetEndorsementMortgageeSectionsSelector(),
		makeAreOneOrMoreEndorsementMortgageeEntityFieldsDisallowedSelector(),
	],
	(
		oldMortgagees: Mortgagee[],
		newMortgagees: Mortgagee[],
		endorsementMortgageeSections: EndorsementSection[],
		areOneOrMoreEndorsementMortgageeEntityFieldsDisallowed: boolean,
	): ComparisonSummaryEntity<Mortgagee>[] => {
		return newMortgagees.filter((newMortgagee: Mortgagee) => {
			return oldMortgagees.some((oldMortgagee: Mortgagee) => {
				return oldMortgagee.id !== null
					&& newMortgagee.id !== null
					&& oldMortgagee.id === newMortgagee.id;
			});
		}).map((currentMortgagee: Mortgagee) => {
			let oldMortgagee: Mortgagee | undefined = oldMortgagees
				.find((oldMortgagee: Mortgagee) => {
					return oldMortgagee.id !== null
						&& currentMortgagee.id !== null
						&& oldMortgagee.id === currentMortgagee.id;
				});

			const matchingMortgageeSection: EndorsementSection | undefined = endorsementMortgageeSections.find((endorsementMortgageeSection: EndorsementSection) => {
				return currentMortgagee.id !== null
					&& endorsementMortgageeSection.key?.toLowerCase() === currentMortgagee.id.toLowerCase();
			});
			const isSectionEditable: boolean = matchingMortgageeSection?.isEditable ?? false;
			const requiresReview: boolean = matchingMortgageeSection?.status?.statusCode === SectionStatus.REQUIRED;

			return new ComparisonSummaryEntity<Mortgagee>({
				id: currentMortgagee.id ?? oldMortgagee?.id ?? String.empty(),
				descriptor: `${currentMortgagee.name}`,
				requiresReview: requiresReview,
				allowEdits: isSectionEditable,
				allowRemoval: !areOneOrMoreEndorsementMortgageeEntityFieldsDisallowed,
				oldData: oldMortgagee,
				newData: currentMortgagee,
			});
		});
	},
);

// Get Endorsement Added Comparison Mortgagee Summaries Selector.
const makeGetEndorsementAddedComparisonMortgageeSummariesSelector = () => createSelector(
	[
		makeGetEndorsementOldMortgageesSelector(),
		makeGetEndorsementNewMortgageesSelector(),
		makeGetEndorsementMortgageeSectionsSelector(),
		makeAreOneOrMoreEndorsementMortgageeEntityFieldsDisallowedSelector(),
	],
	(
		oldMortgagees: Mortgagee[],
		newMortgagees: Mortgagee[],
		endorsementMortgageeSections: EndorsementSection[],
		areOneOrMoreEndorsementMortgageeEntityFieldsDisallowed: boolean,
	): ComparisonSummaryEntity<Mortgagee>[] => {
		return newMortgagees.filter((newMortgagee: Mortgagee) => {
			return !oldMortgagees.some((oldMortgagee: Mortgagee) => {
				return oldMortgagee.id !== null
					&& newMortgagee.id !== null
					&& oldMortgagee.id === newMortgagee.id;
			});
		}).map((addedMortgagee: Mortgagee) => {
			const matchingMortgageeSection: EndorsementSection | undefined = endorsementMortgageeSections.find((endorsementMortgageeSection: EndorsementSection) => {
				return addedMortgagee.id !== null
					&& endorsementMortgageeSection.key?.toLowerCase() === addedMortgagee.id.toLowerCase();
			});
			const isSectionEditable: boolean = matchingMortgageeSection?.isEditable ?? false;
			const requiresReview: boolean = matchingMortgageeSection?.status?.statusCode === SectionStatus.REQUIRED;

			return new ComparisonSummaryEntity<Mortgagee>({
				id: addedMortgagee.id ?? String.empty(),
				descriptor: `${addedMortgagee.name}`,
				requiresReview: requiresReview,
				allowEdits: isSectionEditable,
				allowRemoval: !areOneOrMoreEndorsementMortgageeEntityFieldsDisallowed,
				oldData: undefined,
				newData: addedMortgagee,
			});
		});
	},
);

// Get Endorsement Removed Comparison Mortgagee Summaries Selector.
const makeGetEndorsementRemovedComparisonMortgageeSummariesSelector = () => createSelector(
	[
		makeGetEndorsementOldMortgageesSelector(),
		makeGetEndorsementNewMortgageesSelector(),
	],
	(
		oldMortgagees: Mortgagee[],
		newMortgagees: Mortgagee[],
	): ComparisonSummaryEntity<Mortgagee>[] => {
		return oldMortgagees.filter((oldMortgagee: Mortgagee) => {
			return !newMortgagees.some((newMortgagee: Mortgagee) => {
				return oldMortgagee.id !== null
					&& newMortgagee.id !== null
					&& oldMortgagee.id.toLowerCase() === newMortgagee.id.toLowerCase();
			});
		}).map((removedMortgagee: Mortgagee) => {
			return new ComparisonSummaryEntity<Mortgagee>({
				id: removedMortgagee.id ?? String.empty(),
				descriptor: `${removedMortgagee.name}`,
				requiresReview: false,
				allowEdits: false,
				allowRemoval: false,
				oldData: removedMortgagee,
				newData: undefined,
			});
		});
	},
);

// Get Endorsement Comparison Mortgagee Summaries Selector.
const makeGetEndorsementComparisonMortgageeSummariesSelector = () => createSelector(
	[
		makeGetEndorsementOldMortgageesSelector(),
		makeGetEndorsementCurrentComparisonMortgageeSummariesSelector(),
		makeGetEndorsementAddedComparisonMortgageeSummariesSelector(),
		makeGetEndorsementRemovedComparisonMortgageeSummariesSelector(),
	],
	(
		oldMortgagees: Mortgagee[],
		currentComparisonMortgageeSummaries: ComparisonSummaryEntity<Mortgagee>[],
		addedComparisonMortgageeSummaries: ComparisonSummaryEntity<Mortgagee>[],
		removedComparisonMortgageeSummaries: ComparisonSummaryEntity<Mortgagee>[],
	): ComparisonSummaryEntity<Mortgagee>[] => {

		let mortgageeSummaries: ComparisonSummaryEntity<Mortgagee>[] = oldMortgagees.map((oldMortgagee) => {
			return currentComparisonMortgageeSummaries.find((currentMortgageeSummary) => currentMortgageeSummary.id === oldMortgagee.id)
				?? removedComparisonMortgageeSummaries.find((removedMortgageeSummary) => removedMortgageeSummary.id === oldMortgagee.id);
		}).filter<ComparisonSummaryEntity<Mortgagee>>((mortgageeSummary): mortgageeSummary is ComparisonSummaryEntity<Mortgagee> => {
			return mortgageeSummary !== undefined;
		});

		return [
			...mortgageeSummaries,
			...addedComparisonMortgageeSummaries,
		].map((mortgageeSummary, index) => {
			mortgageeSummary.descriptor = `Mortgagee Number: ${index + 1} - ${mortgageeSummary.descriptor}`;
			return mortgageeSummary;
		});
	},
);

// Get Endorsement Comparison Mortgagees Summary Selector.
const makeGetEndorsementComparisonMortgageesSummarySelector = () => createSelector(
	[
		makeGetEndorsementComparisonMortgageeSummariesSelector(),
		makeGetEndorsementAddMortgageeSectionSelector(),
	],
	(
		comparisonMortgageeSummaries: ComparisonSummaryEntity<Mortgagee>[],
		endorsementAddMortgageeSection: EndorsementSection,
	): ComparisonSummaryMortgagees => {
		return new ComparisonSummaryMortgagees({
			allowAdds: endorsementAddMortgageeSection.isEditable ?? false,
			comparisonEntitySummaries: comparisonMortgageeSummaries,
		});
	},
);

// Get Endorsement Comparison Insured Information Summary Selector.
const makeGetEndorsementComparisonInsuredInformationSummarySelector = () => createSelector(
	[
		makeGetEndorsementOldInsuredInfoSelector(),
		makeGetEndorsementNewInsuredInfoSelector(),
		makeGetEndorsementInsuredSectionSelector(),
	],
	(
		oldInsuredInfo: InsuredInfo | undefined,
		newInsuredInfo: InsuredInfo | undefined,
		endorsementInsuredInformationSection: EndorsementSection,
	): ComparisonSummaryInsuredInfo => {
		return new ComparisonSummaryInsuredInfo({
			allowEdits: endorsementInsuredInformationSection.isEditable ?? false,
			requiresReview: endorsementInsuredInformationSection.status?.statusCode === SectionStatus.REQUIRED,
			oldData: oldInsuredInfo,
			newData: newInsuredInfo,
		});
	},
);

// #endregion Comparison Selectors.

// #region Old Or New Value Selectors.

// Get Endorsement Property State Selector.
const makeGetEndorsementPropertyStateSelector = () => createSelector(
	[makeGetEndorsementOldPropertyStateSelector(), makeGetEndorsementNewPropertyStateSelector()],
	(oldPropertyStateCode: string | undefined, newPropertyStateCode: string | undefined): string | undefined =>
		newPropertyStateCode ?? oldPropertyStateCode
);

// Get Endorsement Policy Information Selector.
const makeGetEndorsementPolicyInformationSelector = () => createSelector(
	[
		(state: AppState, props: EndorsementProps, instanceType: EntityInstanceType) => instanceType,
		makeGetEndorsementSelector(),
	],
	(instanceType: EntityInstanceType, endorsement: EndorsementResponse | undefined): EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined => {
		let policyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined = undefined;

		if (endorsement !== undefined) {
			if (instanceType === EntityInstanceType.NEW) {
				policyInformation = endorsement.policyInformationNew ?? undefined;
			}
			else if (instanceType === EntityInstanceType.OLD) {
				policyInformation = endorsement.policyInformationOld ?? undefined;
			}
		}
		return policyInformation;
	}
);

// Get Endorsement Building Risk Data Personal Selector.
const makeGetEndorsementBuildingRiskDataPersonalSelector = () => createSelector(
	[makeGetEndorsementPolicyInformationSelector()],
	(policyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): BuildingRiskDataPersonal | undefined => {
		let buildingRiskDataPersonal: BuildingRiskDataPersonal | undefined = undefined;
		if (policyInformation !== undefined && EndorsementResponsePolicyPersonal.isEndorsementResponsePolicyPersonal(policyInformation)) {
			buildingRiskDataPersonal = policyInformation.buildingRiskData ?? undefined;
		}
		return buildingRiskDataPersonal;
	}
);

// Get Endorsement Building Replacement Cost Value Personal Selector.
const makeGetEndorsementBuildingReplacementCostValuePersonalSelector = () => createSelector(
	[makeGetEndorsementBuildingRiskDataPersonalSelector()],
	(buildingRiskDataPersonal: BuildingRiskDataPersonal | undefined): number | undefined => {
		return buildingRiskDataPersonal?.buildingReplacementCostValue ?? undefined;
	}
);

// Get Endorsement Building Limits Personal Selector.
const makeGetEndorsementBuildingLimitsPersonalSelector = () => createSelector(
	[makeGetEndorsementPolicyInformationSelector()],
	(policyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): BuildingLimitsPersonal | undefined => {
		let buildingLimitsPersonal: BuildingLimitsPersonal | undefined = undefined;
		if (policyInformation !== undefined && EndorsementResponsePolicyPersonal.isEndorsementResponsePolicyPersonal(policyInformation)) {
			buildingLimitsPersonal = policyInformation.buildingLimitsPersonal ?? undefined;
		}
		return buildingLimitsPersonal;
	}
);

// Get Endorsement Building Limits Personal With Rcv Selector.
const makeGetEndorsementBuildingLimitsPersonalWithRcvSelector = () => createSelector(
	[
		makeGetEndorsementBuildingLimitsPersonalSelector(),
		makeGetEndorsementBuildingReplacementCostValuePersonalSelector(),
	],
	(
		buildingLimitsPersonal: BuildingLimitsPersonal | undefined,
		buildingReplacementCostValue: number | undefined,
	): BuildingLimitsPersonalWithRcv | undefined => {
		let buildingLimitsPersonalWithRcv: BuildingLimitsPersonalWithRcv = new BuildingLimitsPersonalWithRcv();
		buildingLimitsPersonalWithRcv.building = buildingLimitsPersonal?.building ?? null;
		buildingLimitsPersonalWithRcv.buildingReplacementCostValue = buildingReplacementCostValue ?? null;
		buildingLimitsPersonalWithRcv.contents = buildingLimitsPersonal?.contents ?? null;
		buildingLimitsPersonalWithRcv.otherStructures = buildingLimitsPersonal?.otherStructures ?? null;
		buildingLimitsPersonalWithRcv.additionalLivingExpense = buildingLimitsPersonal?.additionalLivingExpense ?? null;
		buildingLimitsPersonalWithRcv.contentsUseReplacementCostValue = buildingLimitsPersonal?.contentsUseReplacementCostValue ?? null;
		return buildingLimitsPersonalWithRcv;
	}
);

// Get Endorsement Property Address Personal Selector.
const makeGetEndorsementPropertyAddressPersonalSelector = () => createSelector(
	[makeGetEndorsementPolicyInformationSelector()],
	(policyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): IAddress | undefined => {
		let propertyAddress: IAddress | undefined = undefined;
		if (policyInformation !== undefined && EndorsementResponsePolicyPersonal.isEndorsementResponsePolicyPersonal(policyInformation)) {
			propertyAddress = policyInformation.propertyAddress?.address ?? undefined;
		}
		return propertyAddress;
	}
);

// Get Endorsement Selected Perils Deductibles Selector.
const makeGetEndorsementSelectedPerilsDeductiblesSelector = () => createSelector(
	[makeGetEndorsementPolicyInformationSelector()],
	(policyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): PerilDeductible[] =>
		policyInformation?.deductibles?.map((deductible) => { return { deductibleCode: deductible.deductibleCode, perilCode: deductible.perilCode } }) ?? [],
	
);

// Get Endorsement Selected Peril Codes Selector.
const makeGetEndorsementSelectedPerilCodesSelector = () => createSelector(
	[makeGetEndorsementSelectedPerilsDeductiblesSelector()],
	(perilsDeductibles: PerilDeductible[]): Peril[] => {
		return perilsDeductibles.map((perilDeductible: PerilDeductible) => {
			return perilDeductible.perilCode !== null
				? perilDeductible.perilCode
				: Peril.NONE;
		}).filter((perilCode: Peril) => {
			return !String.isNullOrWhiteSpace(perilCode);
		});
	}
);

// Get Endorsement Buildings Selector.
const makeGetEndorsementBuildingsSelector = () => createSelector(
	[makeGetEndorsementPolicyInformationSelector()],
	(policyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): BuildingResponseCommercialExisting[] => {
		let buildings: BuildingResponseCommercialExisting[] = [];
		if (policyInformation !== undefined && EndorsementResponsePolicyCommercial.isEndorsementResponsePolicyCommercial(policyInformation)) {
			buildings = policyInformation.buildings ?? [];
		}
		return buildings;
	}
);

// Get Endorsement Buildings With Status Selector.
const makeGetEndorsementBuildingsWithStatusSelector = () => createSelector(
	[makeGetEndorsementBuildingsSelector(), makeGetEndorsementBuildingSectionsSelector()],
	(buildings: BuildingResponseCommercialExisting[], buildingSections: EndorsementSection[]): BuildingResponseCommercialExistingWithStatus[] => {
		// Add status to the buildings.
		return buildings.map((building: BuildingResponseCommercialExisting) => {
			let matchingSection: EndorsementSection | undefined = buildingSections.find((buildingSection: EndorsementSection) => buildingSection.key !== null && buildingSection.key.toLowerCase() === building.id?.toLowerCase());

			let buildingStatus: BuildingStatusInfo = new BuildingStatusInfo();
			buildingStatus.statusCode = matchingSection?.status?.statusCode === SectionStatus.REQUIRED ? RecordStatus.IN_THE_WORKS : RecordStatus.COMPLETE;
			buildingStatus.statusMessage = matchingSection?.status?.statusMessage ?? String.empty();

			let buildingWithStatus: BuildingResponseCommercialExistingWithStatus = new BuildingResponseCommercialExistingWithStatus();
			buildingWithStatus.address = building.address;
			buildingWithStatus.buildingData = building.buildingData;
			buildingWithStatus.id = building.id;
			buildingWithStatus.limits = building.limits;
			buildingWithStatus.status = buildingStatus;

			return buildingWithStatus;
		});
	}
);

// Get Endorsement Mortgagees Selector.
const makeGetEndorsementMortgageesSelector = () => createSelector(
	[makeGetEndorsementPolicyInformationSelector()],
	(policyInformation: EndorsementResponsePolicyPersonal | EndorsementResponsePolicyCommercial | undefined): Mortgagee[] => 
		policyInformation?.mortgagees ?? [],
	
);

// Get Endorsement Mortgagees With Status Selector.
const makeGetEndorsementMortgageesWithStatusSelector = () => createSelector(
	[makeGetEndorsementMortgageesSelector(), makeGetEndorsementMortgageeSectionsSelector()],
	(mortgagees: Mortgagee[], mortgageeSections: EndorsementSection[]): MortgageeWithStatus[] => {
		// Add status to the mortgagees.
		return mortgagees.map((mortgagee: Mortgagee) => {
			let matchingSection: EndorsementSection | undefined = mortgageeSections.find((mortgageeSection: EndorsementSection) => mortgageeSection.key !== null && mortgageeSection.key.toLowerCase() === mortgagee.id?.toLowerCase());

			let mortgageeStatus: MortgageeStatusInfo = new MortgageeStatusInfo();
			mortgageeStatus.statusCode = matchingSection?.status?.statusCode === SectionStatus.REQUIRED ? RecordStatus.IN_THE_WORKS : RecordStatus.COMPLETE;
			mortgageeStatus.statusMessage = matchingSection?.status?.statusMessage ?? String.empty();

			let mortgageeWithStatus: MortgageeWithStatus = new MortgageeWithStatus();
			mortgageeWithStatus.billMortgagee = mortgagee.billMortgagee;
			mortgageeWithStatus.coverageRequiredByMortgagee = mortgagee.coverageRequiredByMortgagee;
			mortgageeWithStatus.email = mortgagee.email;
			mortgageeWithStatus.id = mortgagee.id;
			mortgageeWithStatus.includeAsTheirInterestsMayAppear = mortgagee.includeAsTheirInterestsMayAppear;
			mortgageeWithStatus.includeItsSuccessorsAndOrAssigns = mortgagee.includeItsSuccessorsAndOrAssigns;
			mortgageeWithStatus.lienPositionNumber = mortgagee.lienPositionNumber;
			mortgageeWithStatus.loanNumber = mortgagee.loanNumber;
			mortgageeWithStatus.mailingAddress = mortgagee.mailingAddress;
			mortgageeWithStatus.name = mortgagee.name;
			mortgageeWithStatus.sendPaymentReceiptToEmail = mortgagee.sendPaymentReceiptToEmail;
			mortgageeWithStatus.status = mortgageeStatus;

			return mortgageeWithStatus;
		});
	}
);

// Get Endorsement Building Guids Selector.
const makeGetEndorsementBuildingGuidsSelector = () => createSelector(
	[makeGetEndorsementBuildingsSelector()],
	(buildings: BuildingResponseCommercialExisting[]): string[] => 
		buildings
			.map<string>((building: BuildingResponseCommercialExisting) => building.id ?? String.empty())
			.filter((buildingGuid: string) => !String.isNullOrWhiteSpace(buildingGuid)),
);

// Get Endorsement Mortgagee Guids Selector.
const makeGetEndorsementMortgageeGuidsSelector = () => createSelector(
	[makeGetEndorsementMortgageesSelector()],
	(mortgagees: Mortgagee[]): string[] =>
		mortgagees
			.map<string>((mortgagee: Mortgagee) => mortgagee.id ?? String.empty())
			.filter((mortgageeGuid: string) => !String.isNullOrWhiteSpace(mortgageeGuid)),
);

// Is Endorsement Earthquake Peril Selected Selector.
const makeIsEndorsementEarthquakePerilSelectedSelector = () => createSelector(
	[makeGetEndorsementSelectedPerilCodesSelector()],
	(selectedPerilCodes: Peril[]): boolean =>
		selectedPerilCodes.some((selectedPerilCode: Peril) => selectedPerilCode === Peril.EARTHQUAKE),
);

// Is Endorsement Flood Peril Selected Selector.
const makeIsEndorsementFloodPerilSelectedSelector = () => createSelector(
	[makeGetEndorsementSelectedPerilCodesSelector()],
	(selectedPerilCodes: Peril[]): boolean =>
		selectedPerilCodes.some((selectedPerilCode: Peril) => selectedPerilCode === Peril.FLOOD),
);

// #endregion Old Or New Value Selectors.

// #region Endorsement Values Selectors.

// Get Endorsement Date Selector.
const makeGetEndorsementDateSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): Date | undefined =>
		endorsement !== undefined && endorsement.endorsementDate !== null
			? new Date(endorsement.endorsementDate)
			: undefined,
);

// Get Endorsement Submission Status Errors Selector.
const makeGetEndorsementSubmissionStatusErrorsSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): EndorsementSubmissionStatusError[] =>
		endorsement?.submissionStatusErrors ?? [],
);

// Is Endorsement Premium Calculation Required Selector.
const makeIsEndorsementPremiumCalculationRequiredSelector = () => createSelector(
	[makeGetEndorsementSubmissionStatusErrorsSelector()],
	(statusErrors: EndorsementSubmissionStatusError[]): boolean => 
		statusErrors.some((statusError: EndorsementSubmissionStatusError) => 
			statusError.reason === EndorsementSubmissionStatusReason.CALCULATION_REQUIRED
		),
);

// Is Endorsement In The Works Selector.
const makeIsEndorsementInTheWorksSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): boolean => {
		return endorsement?.status === EndorsementStatus.IN_THE_WORKS;
	}
);

// Is Endorsement Submitted Selector.
const makeIsEndorsementSubmittedSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): boolean => {
		return endorsement?.status === EndorsementStatus.SUBMITTED;
	}
);

// Is Endorsement Declined Selector.
const makeIsEndorsementDeclinedSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): boolean => {
		return endorsement?.status === EndorsementStatus.DECLINED;
	}
);

// Is Endorsement On Hold Selector.
const makeIsEndorsementOnHoldSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): boolean => {
		return endorsement?.status === EndorsementStatus.ON_HOLD;
	}
);

// Is Endorsement Approved Selector.
const makeIsEndorsementApprovedSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): boolean => {
		return endorsement?.status === EndorsementStatus.APPROVED;
	}
);

// Is Endorsement Cancelled Selector.
const makeIsEndorsementCancelledSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): boolean => {
		return endorsement?.status === EndorsementStatus.CANCELLED;
	}
);

// Is Endorsement Bound Or Ready To Bind Selector.
const makeIsEndorsementBoundOrReadyToBindSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): boolean => {
		return endorsement?.status === EndorsementStatus.READY_TO_BIND;
	}
);

// Get Endorsement Annual Premium Cost Selector.
const makeGetEndorsementAnnualPremiumCostSelector = () => createSelector(
	[
		(state: AppState, props: EndorsementProps, instanceType: EntityInstanceType) => instanceType,
		makeGetEndorsementSelector(),
	],
	(instanceType: EntityInstanceType, endorsement: EndorsementResponse | undefined): PolicyCost | undefined => {
		let annualPremiumCost: PolicyCost | undefined = undefined;
		if (instanceType === EntityInstanceType.OLD) {
			annualPremiumCost = endorsement?.endorsementCostValues?.oldPolicyCosts ?? undefined;
		}
		else if (instanceType === EntityInstanceType.NEW) {
			annualPremiumCost = endorsement?.endorsementCostValues?.newPolicyCosts ?? undefined;
		}
		return annualPremiumCost;
	}
);

// Get Endorsement Annual Premium Cost Lines Selector.
const makeGetEndorsementAnnualPremiumCostLinesSelector = () => createSelector(
	[makeGetEndorsementAnnualPremiumCostSelector()],
	(annualPremiumCost: PolicyCost | undefined): PolicyCostLineItem[] => {
		let costLines: PolicyCostLineItem[] = [];
		if (annualPremiumCost !== undefined) {
			// Order the cost lines.
			costLines = annualPremiumCost.lineItems.sort((costLineItem1: PolicyCostLineItem, costLineItem2: PolicyCostLineItem) => {
				if (costLineItem1.lineItemNumber !== null
					&& costLineItem2.lineItemNumber !== null) {
					if (costLineItem1.lineItemNumber < costLineItem2.lineItemNumber) {
						return -1;
					}
					else if (costLineItem1.lineItemNumber > costLineItem2.lineItemNumber) {
						return 1;
					}
				}
				return 0;
			})
		}
		return costLines
	}
);

// Get Endorsement Total Annual Premium Cost Selector.
const makeGetEndorsementTotalAnnualPremiumCostSelector = () => createSelector(
	[makeGetEndorsementAnnualPremiumCostSelector()],
	(annualPremiumCost: PolicyCost | undefined): number | undefined => 
		annualPremiumCost?.totalCost ?? undefined,
);

// Get Endorsement Cost Selector.
const makeGetEndorsementCostSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): PolicyCost | undefined => {
		return endorsement?.endorsementCostValues?.endorsementCost ?? undefined;
	}
);

// Get Endorsement Cost If Not Zero Selector.
const makeGetEndorsementCostIfNotZeroSelector = () => createSelector(
	[makeGetEndorsementCostSelector()],
	(endorsementCost: PolicyCost | undefined): PolicyCost | undefined => {
		return endorsementCost !== undefined
			&& endorsementCost.totalCost !== null
			&& endorsementCost.totalCost !== 0
			? endorsementCost
			: undefined;
	}
);

// Get Endorsement Cost Lines Selector.
const makeGetEndorsementCostLinesSelector = () => createSelector(
	[makeGetEndorsementCostSelector()],
	(endorsementCost: PolicyCost | undefined): PolicyCostLineItem[] => {
		let costLines: PolicyCostLineItem[] = [];
		if (endorsementCost !== undefined) {
			// Order the cost lines.
			costLines = endorsementCost.lineItems.sort((costLineItem1: PolicyCostLineItem, costLineItem2: PolicyCostLineItem) => {
				if (costLineItem1.lineItemNumber !== null
					&& costLineItem2.lineItemNumber !== null) {
					if (costLineItem1.lineItemNumber < costLineItem2.lineItemNumber) {
						return -1;
					}
					else if (costLineItem1.lineItemNumber > costLineItem2.lineItemNumber) {
						return 1;
					}
				}
				return 0;
			})
		}
		return costLines
	}
);

// Get Endorsement Total Cost Selector.
const makeGetEndorsementTotalCostSelector = () => createSelector(
	[makeGetEndorsementCostSelector()],
	(endorsementCost: PolicyCost | undefined): number | undefined => 
		endorsementCost?.totalCost ?? undefined,
);

// Get Endorsement Display Cost Selector.
const makeGetEndorsementDisplayCostSelector = () => createSelector(
	[makeIsEndorsementRenewalSelector(), makeGetEndorsementSelector()],
	(isEndorsementRenewal: boolean, endorsement: EndorsementResponse | undefined): PolicyCost | undefined => {
		return (isEndorsementRenewal
			? endorsement?.endorsementCostValues?.newPolicyCosts
			: endorsement?.endorsementCostValues?.endorsementCost) ?? undefined;
	}
);

// Get Endorsement Display Cost Lines Selector.
const makeGetEndorsementDisplayCostLinesSelector = () => createSelector(
	[makeGetEndorsementDisplayCostSelector()],
	(endorsementCost: PolicyCost | undefined): PolicyCostLineItem[] => {
		let costLines: PolicyCostLineItem[] = [];
		if (endorsementCost !== undefined) {
			// Order the cost lines.
			costLines = endorsementCost.lineItems.sort((costLineItem1: PolicyCostLineItem, costLineItem2: PolicyCostLineItem) => {
				if (costLineItem1.lineItemNumber !== null
					&& costLineItem2.lineItemNumber !== null) {
					if (costLineItem1.lineItemNumber < costLineItem2.lineItemNumber) {
						return -1;
					}
					else if (costLineItem1.lineItemNumber > costLineItem2.lineItemNumber) {
						return 1;
					}
				}
				return 0;
			})
		}
		return costLines
	}
);

// Get Endorsement Display Total Cost Selector.
const makeGetEndorsementDisplayTotalCostSelector = () => createSelector(
	[makeGetEndorsementDisplayCostSelector()],
	(endorsementCost: PolicyCost | undefined): number | undefined =>
		endorsementCost?.totalCost ?? undefined,
);

// Get Allowed Endorsement Date Range Selector.
const makeGetAllowedEndorsementDateRangeSelector = () => createSelector(
	[getEndorsementState],
	(endorsementState: EndorsementState | undefined): AllowedEndorsementDateRange | undefined => 
		endorsementState?.allowedEndorsementDateRange ?? undefined,
	
);

// Get Allowed Renewal Endorsement Date Selector.
const makeGetAllowedRenewalEndorsementDateSelector = () => createSelector(
	[makeGetAllowedEndorsementDateRangeSelector()],
	(allowedEndorsementDateRange: AllowedEndorsementDateRange | undefined): Date | undefined => 
		allowedEndorsementDateRange !== undefined && allowedEndorsementDateRange.renewalDate !== null
			? new Date(allowedEndorsementDateRange.renewalDate)
			: undefined,
	
);

// Get Allowed Maximum Endorsement Date Boundary Selector.
const makeGetAllowedMaximumEndorsementDateBoundarySelector = () => createSelector(
	[makeGetAllowedEndorsementDateRangeSelector()],
	(allowedEndorsementDateRange: AllowedEndorsementDateRange | undefined): Date => 
		allowedEndorsementDateRange !== undefined && allowedEndorsementDateRange.endDate !== null
			? new Date(allowedEndorsementDateRange.endDate)
			: new Date(),
	
);

// Get Allowed Minimum Endorsement Date Boundary Selector.
const makeGetAllowedMinimumEndorsementDateBoundarySelector = () => createSelector(
	[makeGetAllowedEndorsementDateRangeSelector()],
	(allowedEndorsementDateRange: AllowedEndorsementDateRange | undefined): Date => 
		allowedEndorsementDateRange !== undefined && allowedEndorsementDateRange.startDate !== null
			? new Date(allowedEndorsementDateRange.startDate)
			: new Date(),
	
);

// Get Endorsement Possible Perils Selector.
const makeGetEndorsementPossiblePerilsSelector = () => createSelector(
	[getEndorsementState],
	(endorsementState: EndorsementState | undefined): PossiblePeril[] =>
		endorsementState?.endorsementPossiblePerils ?? [],
);

// Is Endorsement Date Valid Selector.
const makeIsEndorsementDateValidSelector = () => createSelector(
	[makeGetEndorsementDateSelector(),
		makeGetAllowedMaximumEndorsementDateBoundarySelector(),
		makeGetAllowedMinimumEndorsementDateBoundarySelector(),
		makeGetAllowedRenewalEndorsementDateSelector(),
	],
	(endorsementDate: Date | undefined,
		allowedMaximumEndorsementDate: Date | undefined,
		allowedMinimumEndorsementDate: Date | undefined,
		allowedRenewalEndorsementDate: Date | undefined): boolean => {
		let isEndorsementDateValid: boolean = true;
		if (endorsementDate !== undefined) {
			if (allowedRenewalEndorsementDate === undefined
				|| endorsementDate.getTime() !== allowedRenewalEndorsementDate.getTime()) {
				if (allowedMaximumEndorsementDate !== undefined
					&& endorsementDate.getTime() > allowedMaximumEndorsementDate.getTime()) {
					isEndorsementDateValid = false;
				}
				if (allowedMinimumEndorsementDate !== undefined
					&& endorsementDate.getTime() < allowedMinimumEndorsementDate.getTime()) {
					isEndorsementDateValid = false;
				}
			}
		}
		else {
			isEndorsementDateValid = false;
		}
		return isEndorsementDateValid;
	}
);

// Get Endorsement Pre-Submission Acknowledgements Selector.
const makeGetEndorsementPreSubmissionAcknowledgementsSelector = () => createSelector(
	[makeGetEndorsementSelector()],
	(endorsement: EndorsementResponse | undefined): EndorsementPreSubmissionAcknowledgements | undefined => 
		endorsement?.preSubmissionAcknowledgements ?? undefined,
);

// Get Endorsement Pre-Submission Acknowledgement Disclosures Selector.
const makeGetEndorsementPreSubmissionAcknowledgementDisclosuresSelector = () => createSelector(
	[makeGetEndorsementPreSubmissionAcknowledgementsSelector()],
	(preSubmissionAcknowledgements: EndorsementPreSubmissionAcknowledgements | undefined): PossibleDisclosureText[] =>
		preSubmissionAcknowledgements?.disclosures ?? [],
);

// Get Endorsement Pre-Submission Unacknowledged Acknowledgement Disclosures Selector.
const makeGetEndorsementPreSubmissionUnacknowledgedAcknowledgementDisclosuresSelector = () => createSelector(
	[makeGetEndorsementPreSubmissionAcknowledgementDisclosuresSelector()],
	(possibleDisclosureTexts: PossibleDisclosureText[]): PossibleDisclosureText[] =>
		possibleDisclosureTexts.filter((disclosure) => !disclosure.isAcknowledged) ?? [],
);

// Are All Endorsement Pre-Submission Acknowledgement Disclosures Acknowledged Selector.
const makeAreAllEndorsementPreSubmissionAcknowledgementDisclosuresAcknowledgedSelector = () => createSelector(
	[makeGetEndorsementPreSubmissionAcknowledgementDisclosuresSelector()],
	(disclosures: PossibleDisclosureText[]): boolean =>
		disclosures.length === 0 || disclosures.every((disclosure) => disclosure.isAcknowledged ?? false),
);

// Get Endorsement Pre-Submission Acknowledgement Disqualifiers Selector.
const makeGetEndorsementPreSubmissionAcknowledgementDisqualifiersSelector = () => createSelector(
	[makeGetEndorsementPreSubmissionAcknowledgementsSelector()],
	(preSubmissionAcknowledgements: EndorsementPreSubmissionAcknowledgements | undefined): DisqualifiersTextWithAcknowledgement | undefined =>
		preSubmissionAcknowledgements?.disqualifierText ?? undefined,
);

// Get Endorsement Pre-Submission Acknowledgement Disqualifiers If Unacknowledged Selector.
const makeGetEndorsementPreSubmissionAcknowledgementDisqualifiersIfUnacknowledgedSelector = () => createSelector(
	[makeGetEndorsementPreSubmissionAcknowledgementDisqualifiersSelector()],
	(disqualifiers: DisqualifiersTextWithAcknowledgement | undefined): DisqualifiersTextWithAcknowledgement | undefined => {
		let disqualifiersToReturn: DisqualifiersTextWithAcknowledgement | undefined = undefined;
		if (disqualifiers !== undefined) {
			let isAcknowledged: boolean = disqualifiers.isAcknowledged !== null ? disqualifiers.isAcknowledged : false;
			if (!isAcknowledged) {
				disqualifiersToReturn = disqualifiers;
			}
		}
		return disqualifiersToReturn;
	},
);

// Are Endorsement Pre-Submission Acknowledgement Disqualifiers Acknowledged Selector.
const makeAreEndorsementPreSubmissionAcknowledgementDisqualifiersAcknowledgedSelector = () => createSelector(
	[makeGetEndorsementPreSubmissionAcknowledgementDisqualifiersSelector()],
	(disqualifiersText: DisqualifiersTextWithAcknowledgement | undefined): boolean =>
		disqualifiersText?.isAcknowledged ?? true,
);


// Are Endorsement Acknowledgements Required Selector.
const makeAreEndorsementAcknowledgementsRequiredSelector = () => createSelector(
	[
		makeAreAllEndorsementPreSubmissionAcknowledgementDisclosuresAcknowledgedSelector(),
		makeAreEndorsementPreSubmissionAcknowledgementDisqualifiersAcknowledgedSelector(),
	],
	(
		areAllEndorsementPreSubmissionAcknowledgementDisclosuresAcknowledged: boolean,
		areEndorsementPreSubmissionAcknowledgementDisqualifiersAcknowledged: boolean
	): boolean => 
		!areAllEndorsementPreSubmissionAcknowledgementDisclosuresAcknowledged
		|| !areEndorsementPreSubmissionAcknowledgementDisqualifiersAcknowledged
);

// Are Endorsement Possible Perils Loaded Selector.
const makeAreEndorsementPossiblePerilsLoadedSelector = () => createSelector(
	[getEndorsementState],
	(endorsementState: EndorsementState | undefined): boolean =>
		endorsementState?.areEndorsementPossiblePerilsLoaded ?? false,
);

// #endregion Endorsement Values Selectors.

// #region Endorsement Entity Field Selectors.

// Get Disallowed Request Fields Selector.
const makeGetDisallowedRequestFieldsSelector = () => createSelector(
	[
		makeGetEndorsementSelector()
	],
	(endorsement: EndorsementResponse | undefined = undefined): DisallowedRequestField[] =>
		endorsement?.disallowedFields ?? [],
);

// Get Disallowed Fields Selector.
const makeGetDisallowedFieldsSelector = () => createSelector(
	[
		makeGetDisallowedRequestFieldsSelector()
	],
	(disallowedRequestFields: DisallowedRequestField[]): EndorsementEntityField[] =>
		disallowedRequestFields
			.map((disallowedRequestField) => disallowedRequestField.requestFieldName ?? EndorsementEntityField.NONE )
			.filter((disallowedField) => disallowedField !== EndorsementEntityField.NONE)
);


// Is Endorsement Entity Field Disallowed Selector.
const makeIsEndorsementEntityFieldDisallowedSelector = () => createSelector(
	[
		(state: AppState, props: EndorsementProps, entityField: EndorsementEntityField) => entityField,
		makeGetDisallowedFieldsSelector()
	],
	(
		entityField: EndorsementEntityField,
		disallowedFields: EndorsementEntityField[],
	): boolean =>
		disallowedFields.some(disallowedField => disallowedField === entityField) ?? false
);

// Are One Or More Endorsement Building Entity Fields Disallowed Selector.
const makeAreOneOrMoreEndorsementBuildingEntityFieldsDisallowedSelector = () => createSelector(
	[
		makeGetDisallowedFieldsSelector()
	],
	(disallowedFields: EndorsementEntityField[]): boolean =>
		EndorsementEntityField.getBuildingRelatedEntityFields().some(relatedEntityField => disallowedFields.includes(relatedEntityField))
);

// Are All Endorsement Building Entity Fields Disallowed Selector.
const makeAreAllEndorsementBuildingEntityFieldsDisallowedSelector = () => createSelector(
	[
		makeGetDisallowedFieldsSelector()
	],
	(disallowedFields: EndorsementEntityField[]): boolean =>
		EndorsementEntityField.getBuildingRelatedEntityFields().every(relatedEntityField => disallowedFields.includes(relatedEntityField))
);

// Are One Or More Endorsement Mortgagee Entity Fields Disallowed Selector.
const makeAreOneOrMoreEndorsementMortgageeEntityFieldsDisallowedSelector = () => createSelector(
	[
		makeGetDisallowedFieldsSelector()
	],
	(disallowedFields: EndorsementEntityField[]): boolean =>
		EndorsementEntityField.getMortgageeRelatedEntityFields().some(relatedEntityField => disallowedFields.includes(relatedEntityField))
);

// Are All Endorsement Mortgagee Entity Fields Disallowed Selector.
const makeAreAllEndorsementMortgageeEntityFieldsDisallowedSelector = () => createSelector(
	[
		makeGetDisallowedFieldsSelector()
	],
	(disallowedFields: EndorsementEntityField[]): boolean =>
		EndorsementEntityField.getMortgageeRelatedEntityFields().every(relatedEntityField => disallowedFields.includes(relatedEntityField))
);

// Are All Endorsement Peril Entity Fields Disallowed Selector.
const makeAreAllEndorsementPerilEntityFieldsDisallowedSelector = () => createSelector(
	[
		makeGetDisallowedFieldsSelector()
	],
	(disallowedFields: EndorsementEntityField[]): boolean =>
		EndorsementEntityField.getPerilRelatedEntityFields().every(relatedEntityField => disallowedFields.includes(relatedEntityField))
);

// Are All Endorsement Dwelling Property Address Entity Fields Disallowed Selector.
const makeAreAllEndorsementDwellingPropertyAddressEntityFieldsDisallowedSelector = () => createSelector(
	[
		makeGetDisallowedFieldsSelector()
	],
	(disallowedFields: EndorsementEntityField[]): boolean =>
		EndorsementEntityField.getDwellingPropertyAddressRelatedEntityFields().every(relatedEntityField => disallowedFields.includes(relatedEntityField))
);

// Are All Endorsement Dwelling Details Entity Fields Disallowed Selector.
const makeAreAllEndorsementDwellingDetailsEntityFieldsDisallowedSelector = () => createSelector(
	[
		makeGetDisallowedFieldsSelector()
	],
	(disallowedFields: EndorsementEntityField[]): boolean =>
		EndorsementEntityField.getDwellingPropertyDetailsRelatedEntityFields().every(relatedEntityField => disallowedFields.includes(relatedEntityField))
);

// Are All Endorsement Dwelling Limit Entity Fields Disallowed Selector.
const makeAreAllEndorsementDwellingLimitEntityFieldsDisallowedSelector = () => createSelector(
	[
		makeGetDisallowedFieldsSelector()
	],
	(disallowedFields: EndorsementEntityField[]): boolean =>
		EndorsementEntityField.getDwellingLimitsRelatedEntityFields().every(relatedEntityField => disallowedFields.includes(relatedEntityField))
);

// Are All Endorsement Insured Information Entity Fields Disallowed Selector.
const makeAreAllEndorsementInsuredInformationEntityFieldsDisallowedSelector = () => createSelector(
	[
		makeGetDisallowedFieldsSelector()
	],
	(disallowedFields: EndorsementEntityField[]): boolean =>
		EndorsementEntityField.getInsuredRelatedEntityFields().every(relatedEntityField => disallowedFields.includes(relatedEntityField))
);

// #endregion Endorsement Entity Field Selectors.

// #region Endorsement Entity Field Input Props Selectors.

// Get Endorsement Subform Dwelling Info Input Setting Props Selector.
const makeGetEndorsementSubformDwellingInfoInputSettingPropsSelector = () => createSelector(
	[
		makeGetDisallowedRequestFieldsSelector(),
	],
	(
		disallowedRequestFields: DisallowedRequestField[],
	): SubformInputSettingsDwellingInfoProps => {

		const propertyBuildingUseDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_BUILDING_USE);
		const propertyConstructionTypeDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_CONSTRUCTION_TYPE);
		const propertyFoundationTypeDetailDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_FOUNDATION_TYPE_DETAIL);
		const propertyNumberOfStoriesAboveGradeDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_NUMBER_STORIES_ABOVE_GRADE);
		const propertySquareFeetDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_SQUARE_FEET);
		const propertyYearOfConstructionDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_YEAR_OF_CONSTRUCTION);
		const propertyRcvDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_RCV);
		const propertyFloodZoneDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_FLOOD_ZONE);
		const propertyOccupancyDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_OCCUPANCY_TYPE);
		const propertyIsBelowGradeOrEnclosedAreaFinishedDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_IS_BELOW_GRADE_OR_ENCLOSED_AREA_FINISHED);
		const propertyIsGarageAttachedDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_IS_GARAGE_ATTACHED);
		const propertyYearRoofLastUpdateDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_YEAR_ROOF_LAST_UPDATE);

		return {
			buildingUseInput: propertyBuildingUseDisallowedRequestField !== undefined
				? (propertyBuildingUseDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			constructionTypeInput: propertyConstructionTypeDisallowedRequestField !== undefined
				? (propertyConstructionTypeDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			foundationTypeInput: propertyFoundationTypeDetailDisallowedRequestField !== undefined
				? (propertyFoundationTypeDetailDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			numberOfStoriesInput: propertyNumberOfStoriesAboveGradeDisallowedRequestField !== undefined
				? (propertyNumberOfStoriesAboveGradeDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			squareFootageInput: propertySquareFeetDisallowedRequestField !== undefined
				? (propertySquareFeetDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			yearOfConstructionInput: propertyYearOfConstructionDisallowedRequestField !== undefined
				? (propertyYearOfConstructionDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			buildingReplacementCostValueInput: propertyRcvDisallowedRequestField !== undefined
				? (propertyRcvDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			floodZoneInput: propertyFloodZoneDisallowedRequestField !== undefined
				? (propertyFloodZoneDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			occupancyTypeInput: propertyOccupancyDisallowedRequestField !== undefined
				? (propertyOccupancyDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			belowGradeOrEnclosedAreaFinishedInput: propertyIsBelowGradeOrEnclosedAreaFinishedDisallowedRequestField !== undefined
				? (propertyIsBelowGradeOrEnclosedAreaFinishedDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			garageAttachedInput: propertyIsGarageAttachedDisallowedRequestField !== undefined
				? (propertyIsGarageAttachedDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			yearRoofLastUpdatedInput: propertyYearRoofLastUpdateDisallowedRequestField !== undefined
				? (propertyYearRoofLastUpdateDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',

		};
	}
);

// Get Endorsement Subform Building Info Input Setting Props Selector.
const makeGetEndorsementSubformBuildingInfoInputSettingPropsSelector = () => createSelector(
	[
		makeGetDisallowedRequestFieldsSelector(),
	],
	(
		disallowedRequestFields: DisallowedRequestField[],
	): SubformInputSettingsBuildingInfoProps => {

		const buildingBuildingUseDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_BUILDING_USE);
		const buildingConstructionTypeDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_CONSTRUCTION_TYPE);
		const buildingFoundationTypeDetailDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_FOUNDATION_TYPE_DETAIL);
		const buildingNumberOfStoriesAboveGradeDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_NUMBER_STORIES_ABOVE_GRADE);
		const buildingSquareFeetDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_SQUARE_FEET);
		const buildingYearOfConstructionDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_YEAR_OF_CONSTRUCTION);
		const buildingRcvDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_RCV);
		const buildingFloodZoneDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_FLOOD_ZONE);
		const buildingDescriptionDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_DESCRIPTION);
		const buildingParkingTypeDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_PARKING_TYPE);

		return {
			buildingUseInput: buildingBuildingUseDisallowedRequestField !== undefined
				? (buildingBuildingUseDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			constructionTypeInput: buildingConstructionTypeDisallowedRequestField !== undefined
				? (buildingConstructionTypeDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			foundationTypeInput: buildingFoundationTypeDetailDisallowedRequestField !== undefined
				? (buildingFoundationTypeDetailDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			numberOfStoriesInput: buildingNumberOfStoriesAboveGradeDisallowedRequestField !== undefined
				? (buildingNumberOfStoriesAboveGradeDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			squareFootageInput: buildingSquareFeetDisallowedRequestField !== undefined
				? (buildingSquareFeetDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			yearOfConstructionInput: buildingYearOfConstructionDisallowedRequestField !== undefined
				? (buildingYearOfConstructionDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			buildingReplacementCostValueInput: buildingRcvDisallowedRequestField !== undefined
				? (buildingRcvDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			floodZoneInput: buildingFloodZoneDisallowedRequestField !== undefined
				? (buildingFloodZoneDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			buildingDescriptionInput: buildingDescriptionDisallowedRequestField !== undefined
				? (buildingDescriptionDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			parkingTypeInput: buildingParkingTypeDisallowedRequestField !== undefined
				? (buildingParkingTypeDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
		};
	}
);

// Get Endorsement Subform Building Info Address Input Setting Props Selector.
const makeGetEndorsementSubformBuildingInfoAddressInputSettingPropsSelector = () => createSelector(
	[
		makeGetDisallowedRequestFieldsSelector(),
	],
	(
		disallowedRequestFields: DisallowedRequestField[],
	): SubformInputSettingsBuildingInfoAddressProps => {

		const buildingAddressDisallowedRequestFields = disallowedRequestFields.filter(disallowedRequestField =>
			disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_ADDRESS_1
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_CITY
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_STATE
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_ZIP);

		return {
			buildingAddressSummaryInput: buildingAddressDisallowedRequestFields.length > 0
				? (buildingAddressDisallowedRequestFields.some((buildingAddressDisallowedRequestField) => buildingAddressDisallowedRequestField.hideRequestFieldFromUsers) ? 'hidden' : 'disabled')
				: 'none',
		};
	}
);

// Get Endorsement Subform Building Limits Input Setting Props Selector.
const makeGetEndorsementSubformBuildingLimitsInputSettingPropsSelector = () => createSelector(
	[
		makeGetDisallowedRequestFieldsSelector(),
	],
	(
		disallowedRequestFields: DisallowedRequestField[],
	): SubformInputSettingsBuildingLimitsProps => {

		const buildingBuildingLimitDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_BUILDING_LIMIT);
		const buildingContentsLimitDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_CONTENTS_LIMIT);
		const businessIncomeExtraExpenseLimitDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.BUILDING_BIEE_LIMIT);


		return {
			buildingLimitInput: buildingBuildingLimitDisallowedRequestField !== undefined
				? (buildingBuildingLimitDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			contentsLimitInput: buildingContentsLimitDisallowedRequestField !== undefined
				? (buildingContentsLimitDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			businessIncomeExtraExpenseLimitInput: businessIncomeExtraExpenseLimitDisallowedRequestField !== undefined
				? (businessIncomeExtraExpenseLimitDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
		};
	}
);

// Get Endorsement Subform Property Address Input Setting Props Selector.
const makeGetEndorsementSubformPropertyAddressInputSettingPropsSelector = () => createSelector(
	[
		makeGetDisallowedRequestFieldsSelector()
	],
	(disallowedRequestFields: DisallowedRequestField[]): SubformInputSettingsPropertyAddressProps => {

		const propertyAddressDisallowedRequestFields = disallowedRequestFields.filter(disallowedRequestField =>
			disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_ADDRESS_1
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_ADDRESS_2
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_CITY
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_STATE
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_ZIP);

		return {
			addressInput: propertyAddressDisallowedRequestFields.length > 0
				? (propertyAddressDisallowedRequestFields.some((propertyAddressDisallowedRequestField) => propertyAddressDisallowedRequestField.hideRequestFieldFromUsers) ? 'hidden' : 'disabled')
				: 'none',
			copyToMailingAddressInput: 'hidden',
		};
	}
);

// Get Endorsement Form Building Full Input Setting Props Selector.
const makeGetEndorsementFormBuildingFullInputSettingPropsSelector = () => createSelector(
	[
		makeGetEndorsementSubformPropertyAddressInputSettingPropsSelector(),
		makeGetEndorsementSubformBuildingInfoInputSettingPropsSelector(),
		makeGetEndorsementSubformBuildingLimitsInputSettingPropsSelector(),
	],
	(
		subformPropertyAddressInputSettingsProps: SubformInputSettingsPropertyAddressProps,
		subformBuildingInfoInputSettingProps: SubformInputSettingsBuildingInfoProps,
		subformBuildingLimitsInputSettingsProps: SubformInputSettingsBuildingLimitsProps,
	): FormInputSettingsBuildingFullProps => {

		return {
			subformInputSettingsPropertyAddressProps: subformPropertyAddressInputSettingsProps,
			subformInputSettingsBuildingInfoProps: subformBuildingInfoInputSettingProps,
			subformInputSettingsBuildingLimitsProps: subformBuildingLimitsInputSettingsProps,
		};
	}
);

// Get Endorsement Form Dwelling Info Input Setting Props Selector.
const makeGetEndorsementFormDwellingInfoInputSettingPropsSelector = () => createSelector(
	[
		makeGetEndorsementSubformDwellingInfoInputSettingPropsSelector(),
	],
	(
		subformDwellingInfoInputSettingProps: SubformInputSettingsDwellingInfoProps
	): FormInputSettingsDwellingInfoProps => {

		return {
			subformInputSettingsPropertyInfoProps: subformDwellingInfoInputSettingProps,
		};
	}
);

// Get Endorsement Form Building Info Input Setting Props Selector.
const makeGetEndorsementFormBuildingInfoInputSettingPropsSelector = () => createSelector(
	[
		makeGetEndorsementSubformBuildingInfoInputSettingPropsSelector(),
		makeGetEndorsementSubformBuildingInfoAddressInputSettingPropsSelector(),
	],
	(
		subformBuildingInfoInputSettingProps: SubformInputSettingsBuildingInfoProps,
		subformBuildingInfoAddressInputSettingProps: SubformInputSettingsBuildingInfoAddressProps,
	): FormInputSettingsBuildingInfoProps => {

		return {
			subformInputSettingsPropertyInfoProps: subformBuildingInfoInputSettingProps,
			subformInputSettingsBuildingInfoAddressProps: subformBuildingInfoAddressInputSettingProps,
		};
	}
);


// Get Endorsement Dwelling Address Input Setting Props Selector.
const makeGetEndorsementDwellingAddressInputSettingPropsSelector = () => createSelector(
	[
		makeGetEndorsementSubformPropertyAddressInputSettingPropsSelector()
	],
	(subformInputSettingsPropertyAddressProps: SubformInputSettingsPropertyAddressProps): FormInputSettingsPropertyAddressProps => {
		return {
			subformInputSettingsPropertyAddressProps: subformInputSettingsPropertyAddressProps
		};
	}
);

// Get Endorsement Coverage Limits And Deductibles Input Settings Props Selector.
const makeGetEndorsementCoverageLimitsAndDeductiblesInputSettingsPropsSelector = () => createSelector(
	[
		makeGetDisallowedRequestFieldsSelector(),
	],
	(disallowedRequestFields: DisallowedRequestField[]): FormInputSettingsCoverageLimitsAndDeductiblesProps => {
		const deductiblesDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.DEDUCTIBLES);
		return {
			perilDeductibleInputs: deductiblesDisallowedRequestField !== undefined
				? (deductiblesDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
		};
	}
);

// Get Endorsement Coverage Limits And Deductibles Personal Input Settings Props Selector.
const makeGetEndorsementCoverageLimitsAndDeductiblesPersonalInputSettingsPropsSelector = () => createSelector(
	[
		makeGetDisallowedRequestFieldsSelector(),
		makeGetEndorsementCoverageLimitsAndDeductiblesInputSettingsPropsSelector()
	],
	(
		disallowedRequestFields: DisallowedRequestField[],
		inputSettingsProps: FormInputSettingsCoverageLimitsAndDeductiblesProps,
	): FormInputSettingsCoverageLimitsAndDeductiblesPersonalProps => {

		const propertyBuildingLimitDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_BUILDING_LIMIT);
		const propertyContentsLimitDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_CONTENTS_LIMIT);
		const propertyContentsUseRcvDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_CONTENTS_USE_RCV);
		const propertyOtherStructuresLimitDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_OTHER_STRUCTURES_LIMIT);
		const propertyALELimitDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.PROPERTY_ALE_LIMIT);

		return {
			...inputSettingsProps,
			buildingLimitInput: propertyBuildingLimitDisallowedRequestField !== undefined
				? (propertyBuildingLimitDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			contentsLimitInput: propertyContentsLimitDisallowedRequestField !== undefined
				? (propertyContentsLimitDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			personalPropertyLossAdjustmentBasesInput: propertyContentsUseRcvDisallowedRequestField !== undefined
				? (propertyContentsUseRcvDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			otherStructuresLimitInput: propertyOtherStructuresLimitDisallowedRequestField !== undefined
				? (propertyOtherStructuresLimitDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			additionalLivingExpenseLimitInput: propertyALELimitDisallowedRequestField !== undefined
				? (propertyALELimitDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
		};
	}
);

// Get Endorsement Insured Info Input Setting Props Selector.
const makeGetEndorsementInsuredInfoInputSettingPropsSelector = () => createSelector(
	[
		makeGetDisallowedRequestFieldsSelector(),
	],
	(disallowedRequestFields: DisallowedRequestField[]): FormInputSettingsInsuredInfoProps => {
		const insuredAddressDisallowedRequestFields = disallowedRequestFields.filter(disallowedRequestField =>
			disallowedRequestField.requestFieldName === EndorsementEntityField.INSURED_ADDRESS_1
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.INSURED_ADDRESS_2
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.INSURED_CITY
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.INSURED_STATE
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.INSURED_ZIP);
		const insuredNameDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.INSURED_FIRST);
		const insuredLastNameDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.INSURED_LAST);
		const insuredHomePhoneDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.INSURED_PHONE);
		const insuredMobilePhoneDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.INSURED_PHONE);
		const insuredEmailDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.INSURED_EMAIL);
		const sendPaymentReceiptDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.INSURED_EMAIL);

		return {
			insuredAddressInput: insuredAddressDisallowedRequestFields.length > 0
				? (insuredAddressDisallowedRequestFields.some((insuredAddressDisallowedRequestField) => insuredAddressDisallowedRequestField.hideRequestFieldFromUsers) ? 'hidden' : 'disabled')
				: 'none',
			insuredNameInput: insuredNameDisallowedRequestField !== undefined
				? (insuredNameDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			insuredLastInput: insuredLastNameDisallowedRequestField !== undefined
				? (insuredLastNameDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			insuredHomePhoneInput: insuredHomePhoneDisallowedRequestField !== undefined
				? (insuredHomePhoneDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			insuredMobilePhoneInput: insuredMobilePhoneDisallowedRequestField !== undefined
				? (insuredMobilePhoneDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			insuredEmailInput: insuredEmailDisallowedRequestField !== undefined
				? (insuredEmailDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			sendPaymentReceiptToEmailInput: sendPaymentReceiptDisallowedRequestField !== undefined
				? (sendPaymentReceiptDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
		};
	}
);

// Get Endorsement Mortgagee Input Setting Props Selector.
const makeGetEndorsementMortgageeInputSettingPropsSelector = () => createSelector(
	[
		makeGetDisallowedRequestFieldsSelector()
	],
	(disallowedRequestFields: DisallowedRequestField[]): FormInputSettingsMortgageeProps => {

		const mortgageeNameDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_NAME);
		const mortgageeAddressDisallowedRequestFields = disallowedRequestFields.filter(disallowedRequestField =>
			disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_ADDRESS_1
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_ADDRESS_2
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_CITY
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_STATE
			|| disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_ZIP);
		const mortgageeIncludeAsTheirInterestsMayAppearDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_INCLUDE_AS_THEIR_INTERESTS_MAY_APPEAR);
		const mortgageeIncludeItsSuccessorsAndOrAssignsDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_INCLUDE_ITS_SUCCESSORS_AND_OR_ASSIGNS);
		const mortgageeLienPositionNumberDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_LIEN_POSITION_NUMBER);
		const mortgageeLoanNumberDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_LOAN_NUMBER);
		const mortgageeEmailDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_EMAIL);
		const sendPaymentReceiptToMortgageeRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_EMAIL);
		const mortgageeBillMortgageeDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_BILL_MORTGAGEE);
		const mortgageeIsMortgageeRequiredDisallowedRequestField = disallowedRequestFields.find(disallowedRequestField => disallowedRequestField.requestFieldName === EndorsementEntityField.MORTGAGEE_IS_MORTGAGEE_REQUIRED);


		return {
			mortgageeNameInput: mortgageeNameDisallowedRequestField !== undefined
				? (mortgageeNameDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			mortgageeAddressInput: mortgageeAddressDisallowedRequestFields.length > 0
				? (mortgageeAddressDisallowedRequestFields.some((mortgageeAddressDisallowedRequestField) => mortgageeAddressDisallowedRequestField.hideRequestFieldFromUsers) ? 'hidden' : 'disabled')
				: 'none',
			includeInterestsInput: mortgageeIncludeAsTheirInterestsMayAppearDisallowedRequestField !== undefined
				? (mortgageeIncludeAsTheirInterestsMayAppearDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			includeSuccessorOrAssignsInput: mortgageeIncludeItsSuccessorsAndOrAssignsDisallowedRequestField !== undefined
				? (mortgageeIncludeItsSuccessorsAndOrAssignsDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			lienholderPositionInput: mortgageeLienPositionNumberDisallowedRequestField !== undefined
				? (mortgageeLienPositionNumberDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			loanNumberInput: mortgageeLoanNumberDisallowedRequestField !== undefined
				? (mortgageeLoanNumberDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			mortgageeEmailInput: mortgageeEmailDisallowedRequestField !== undefined
				? (mortgageeEmailDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			sendPaymentReceiptToMortgageeInput: sendPaymentReceiptToMortgageeRequestField !== undefined
				? (sendPaymentReceiptToMortgageeRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			mortgageeBilledInput: mortgageeBillMortgageeDisallowedRequestField !== undefined
				? (mortgageeBillMortgageeDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
			mortgageeRequiredInput: mortgageeIsMortgageeRequiredDisallowedRequestField !== undefined
				? (mortgageeIsMortgageeRequiredDisallowedRequestField.hideRequestFieldFromUsers ? 'hidden' : 'disabled')
				: 'none',
		};
	}
);

// #endregion Endorsement Entity Field Input Props Selectors.

// Export composed methods.
export {
	getEndorsementState,
};

// export selectors.
export {
	// Old and New Value Selectors.
	makeIsEndorsementLoadedSelector,
	makeGetEndorsementSelector,
	makeCanEndorsementAddBuildingsSelector,
	makeCanEndorsementRemoveBuildingsSelector,
	makeIsEndorsementRenewalSelector,
	makeGetEndorsementOldPolicyInformationSelector,
	makeGetEndorsementNewPolicyInformationSelector,
	makeGetEndorsementOldSelectedPerilsDeductiblesSelector,
	makeGetEndorsementNewSelectedPerilsDeductiblesSelector,
	makeGetEndorsementOldSelectedPerilCodesSelector,
	makeGetEndorsementNewSelectedPerilCodesSelector,
	makeGetEndorsementNewAndOldUnionSelectedPerilCodesSelector,
	makeGetEndorsementOldPropertyAddressLocationWithAccuracyPersonalSelector,
	makeGetEndorsementNewPropertyAddressLocationWithAccuracyPersonalSelector,
	makeGetEndorsementOldPropertyAddressPersonalSelector,
	makeGetEndorsementNewPropertyAddressPersonalSelector,
	makeGetEndorsementOldBuildingRiskDataPersonalSelector,
	makeGetEndorsementNewBuildingRiskDataPersonalSelector,
	makeGetEndorsementOldBuildingLimitsPersonalSelector,
	makeGetEndorsementNewBuildingLimitsPersonalSelector,
	makeGetEndorsementOldHasFloodPerilSelectedSelector,
	makeGetEndorsementNewHasFloodPerilSelectedSelector,
	makeGetEndorsementOldHasEarthquakePerilSelectedSelector,
	makeGetEndorsementNewHasEarthquakePerilSelectedSelector,
	makeGetEndorsementOldBuildingsSelector,
	makeGetEndorsementNewBuildingsSelector,
	makeGetEndorsementNewBuildingGuidsSelector,
	makeGetEndorsementOldBuildingSelector,
	makeGetEndorsementNewBuildingSelector,
	makeGetEndorsementOldBuildingAddressLocationWithAccuracyCommercialSelector,
	makeGetEndorsementNewBuildingAddressLocationWithAccuracyCommercialSelector,
	makeGetEndorsementOldBuildingAddressCommercialSelector,
	makeGetEndorsementNewBuildingAddressCommercialSelector,
	makeGetEndorsementOldMortgageesSelector,
	makeGetEndorsementNewMortgageesSelector,
	makeGetEndorsementNewMortgageeGuidsSelector,
	makeGetEndorsementNewMortgageeLienholderPositionsInUseSelector,
	makeGetEndorsementNewMortgageeEmailsInUseSelector,
	makeGetEndorsementNewMortgageeSelector,
	makeGetEndorsementNewMortgageeAsRequestSelector,
	makeGetEndorsementOldPropertyStateSelector,
	makeGetEndorsementNewPropertyStateSelector,
	makeGetEndorsementOldInsuredInfoSelector,
	makeGetEndorsementNewInsuredInfoSelector,
	// Comparison Selectors.
	makeGetEndorsementCurrentComparisonPerilDecuctibleSummariesSelector,
	makeGetEndorsementAddedComparisonPerilDecuctibleSummariesSelector,
	makeGetEndorsementRemovedComparisonPerilDecuctibleSummariesSelector,
	makeGetEndorsementComparisonPerilDeductibleSummariesOrderedSelector,
	makeGetEndorsementComparisonPerilsDeductiblesSummarySelector,
	makeGetEndorsementComparisonDwellingAddressSummarySelector,
	makeGetEndorsementComparisonDwellingDetailsSummarySelector,
	makeGetEndorsementComparisonDwellingLimitsSummarySelector,
	makeGetEndorsementCurrentComparisonBuildingSummariesSelector,
	makeGetEndorsementAddedComparisonBuildingSummariesSelector,
	makeGetEndorsementRemovedComparisonBuildingSummariesSelector,
	makeGetEndorsementComparisonBuildingSummariesSelector,
	makeGetEndorsementComparisonBuildingsSummarySelector,
	makeGetEndorsementCurrentComparisonMortgageeSummariesSelector,
	makeGetEndorsementAddedComparisonMortgageeSummariesSelector,
	makeGetEndorsementRemovedComparisonMortgageeSummariesSelector,
	makeGetEndorsementComparisonMortgageeSummariesSelector,
	makeGetEndorsementComparisonMortgageesSummarySelector,
	makeGetEndorsementComparisonInsuredInformationSummarySelector,
	// Old Or New Value Selectors.
	makeGetEndorsementPropertyStateSelector,
	makeGetEndorsementPolicyInformationSelector,
	makeGetEndorsementBuildingRiskDataPersonalSelector,
	makeGetEndorsementBuildingReplacementCostValuePersonalSelector,
	makeGetEndorsementBuildingLimitsPersonalSelector,
	makeGetEndorsementBuildingLimitsPersonalWithRcvSelector,
	makeGetEndorsementPropertyAddressPersonalSelector,
	makeGetEndorsementSelectedPerilsDeductiblesSelector,
	makeGetEndorsementSelectedPerilCodesSelector,
	makeGetEndorsementBuildingsSelector,
	makeGetEndorsementBuildingsWithStatusSelector,
	makeGetEndorsementMortgageesSelector,
	makeGetEndorsementMortgageesWithStatusSelector,
	makeGetEndorsementBuildingGuidsSelector,
	makeGetEndorsementMortgageeGuidsSelector,
	makeIsEndorsementEarthquakePerilSelectedSelector,
	makeIsEndorsementFloodPerilSelectedSelector,
	// Endorsement Values Selectors.
	makeGetEndorsementDateSelector,
	makeGetEndorsementSubmissionStatusErrorsSelector,
	makeIsEndorsementPremiumCalculationRequiredSelector,
	makeIsEndorsementInTheWorksSelector,
	makeIsEndorsementSubmittedSelector,
	makeIsEndorsementDeclinedSelector,
	makeIsEndorsementOnHoldSelector,
	makeIsEndorsementApprovedSelector,
	makeIsEndorsementCancelledSelector,
	makeIsEndorsementBoundOrReadyToBindSelector,
	makeGetEndorsementAnnualPremiumCostSelector,
	makeGetEndorsementAnnualPremiumCostLinesSelector,
	makeGetEndorsementTotalAnnualPremiumCostSelector,
	makeGetEndorsementCostSelector,
	makeGetEndorsementCostIfNotZeroSelector,
	makeGetEndorsementCostLinesSelector,
	makeGetEndorsementTotalCostSelector,
	makeGetEndorsementDisplayCostSelector,
	makeGetEndorsementDisplayCostLinesSelector,
	makeGetEndorsementDisplayTotalCostSelector,
	makeGetAllowedEndorsementDateRangeSelector,
	makeGetAllowedRenewalEndorsementDateSelector,
	makeGetAllowedMaximumEndorsementDateBoundarySelector,
	makeGetAllowedMinimumEndorsementDateBoundarySelector,
	makeGetEndorsementPossiblePerilsSelector,
	makeIsEndorsementDateValidSelector,
	makeGetEndorsementPreSubmissionAcknowledgementsSelector,
	makeGetEndorsementPreSubmissionAcknowledgementDisclosuresSelector,
	makeGetEndorsementPreSubmissionUnacknowledgedAcknowledgementDisclosuresSelector,
	makeAreAllEndorsementPreSubmissionAcknowledgementDisclosuresAcknowledgedSelector,
	makeGetEndorsementPreSubmissionAcknowledgementDisqualifiersSelector,
	makeGetEndorsementPreSubmissionAcknowledgementDisqualifiersIfUnacknowledgedSelector,
	makeAreEndorsementPreSubmissionAcknowledgementDisqualifiersAcknowledgedSelector,
	makeAreEndorsementAcknowledgementsRequiredSelector,
	makeAreEndorsementPossiblePerilsLoadedSelector,
	// Endorsement Entity Field Selectors.
	makeGetDisallowedRequestFieldsSelector,
	makeGetDisallowedFieldsSelector,
	makeIsEndorsementEntityFieldDisallowedSelector,
	makeAreOneOrMoreEndorsementBuildingEntityFieldsDisallowedSelector,
	makeAreAllEndorsementBuildingEntityFieldsDisallowedSelector,
	makeAreOneOrMoreEndorsementMortgageeEntityFieldsDisallowedSelector,
	makeAreAllEndorsementMortgageeEntityFieldsDisallowedSelector,
	makeAreAllEndorsementPerilEntityFieldsDisallowedSelector,
	makeAreAllEndorsementDwellingPropertyAddressEntityFieldsDisallowedSelector,
	makeAreAllEndorsementDwellingDetailsEntityFieldsDisallowedSelector,
	makeAreAllEndorsementDwellingLimitEntityFieldsDisallowedSelector,
	makeAreAllEndorsementInsuredInformationEntityFieldsDisallowedSelector,
	// Endorsement Entity Field Input Props Selectors
	makeGetEndorsementSubformDwellingInfoInputSettingPropsSelector,
	makeGetEndorsementSubformBuildingInfoInputSettingPropsSelector,
	makeGetEndorsementSubformBuildingInfoAddressInputSettingPropsSelector,
	makeGetEndorsementSubformBuildingLimitsInputSettingPropsSelector,
	makeGetEndorsementSubformPropertyAddressInputSettingPropsSelector,
	makeGetEndorsementFormBuildingFullInputSettingPropsSelector,
	makeGetEndorsementFormDwellingInfoInputSettingPropsSelector,
	makeGetEndorsementFormBuildingInfoInputSettingPropsSelector,
	makeGetEndorsementDwellingAddressInputSettingPropsSelector,
	makeGetEndorsementCoverageLimitsAndDeductiblesInputSettingsPropsSelector,
	makeGetEndorsementCoverageLimitsAndDeductiblesPersonalInputSettingsPropsSelector,
	makeGetEndorsementInsuredInfoInputSettingPropsSelector,
	makeGetEndorsementMortgageeInputSettingPropsSelector,
};